import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  HostListener,
  inject,
  signal,
  computed,
  ChangeDetectorRef,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { Select } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { MessageModule } from 'primeng/message';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable, Subscription, from, merge } from 'rxjs';
import {
  map,
  finalize,
  tap,
  distinctUntilChanged,
  filter,
  take,
  debounceTime,
  switchMap,
} from 'rxjs/operators';

import {
  FilingFormStateService,
  FilingFormState,
  SheetLevelStatusData,
  SheetLevelStatusMap,
} from '../../../services/mbrs/filing-form-state.service';
import {
  RetrievedMbrsData,
  RetrieveMbrsDataService,
} from '../../../services/mbrs/retrieve-mbrs-data.service';
import {
  LoadFailureNotice,
  LoadTrace,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import {
  CONTAINER_LOAD_STEPS,
  DIAGNOSTIC_TOAST_LIFE_MS,
  LOAD_FAILURE_COPY,
  LoadFailureClass,
  SILENT_FAILURE_CLASSES,
  formatToastDiagnosticCode,
} from '../../../utils/sheet-load-trace.util';
import { SchemaAssetError, fetchJsonAsset } from '../../../utils/schema-asset.util';
import { RuntimeConfigService } from '../../../services/common/runtime-config.service';
import { ROUTE_RELOAD_HOP_URL } from '../../common/route-reload-hop/route-reload-hop.component';
import { FilingLoadFailurePanelComponent } from './filing-load-failure-panel/filing-load-failure-panel.component';
import { buildPageLoadFailureNotice } from '../../../utils/page-load-failure.util';
import { XBRL_FILES_URL } from '../../../constants/route-paths';
import { PageLoadFailureToastGate } from '../../../utils/page-load-failure-toast-gate.util';
import { AuthFailureNoticeService } from '../../../services/auth/auth-failure-notice.service';
import { SessionGateService } from '../../../services/auth/session-gate.service';
import { PreviousUrlTrackerService } from '../../../services/common/previous-url-tracker.service';
import { FilingModeService } from '../../../services/mbrs/filing-mode.service';
import { SheetDependencyService } from '../../../services/mbrs/sheet-dependency.service';
import { ConversionValidatorService } from '../../../services/mbrs/conversion-validator.service';
import { FilingFormPopulationService } from '../../../services/mbrs/filing-form-population.service';
import { MbrsSheetData } from '../../../../types/xbrl-preview';
import {
  ConversionAuditTrail,
  ConversionData,
  ConversionPhaseEnum,
  MbrsStatusEnum,
} from '../../../../types/commons';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { buildSubmitSlugToStateKey } from '../../../models/sheet-descriptors';
import {
  SheetType,
  SheetOption,
  SHEET_DISPLAY_NAMES,
} from '../../../models/sheet-types';
import { SheetApplicabilityService } from '../../../services/mbrs/sheet-applicability.service';
import { FIELD_LINK_SCROLL_PARAM } from '../../../utils/field-link-navigation.util';
import { DiagnosticScope, filingSheetScope, scopeFromFilingEntryState } from '../../../utils/navigation-diagnostic-scope.util';
import { Role0000000FilingPrerequisitesComponent } from '../role0-000000-filing-prerequisites/role0-000000-filing-prerequisites.component';
import { Role1010000FilingInfoComponent } from '../role1-010000-filing-info/role1-010000-filing-info.component';
import { Role2020000ScopeOfFilingComponent } from '../role2-020000-scope-of-filing/role2-020000-scope-of-filing.component';
import { Role3110000StockExchangeComponent } from '../role3-110000-stock-exchange/role3-110000-stock-exchange.component';
import { Role4120000DirectorsReportComponent } from '../role4-120000-directors-report/role4-120000-directors-report.component';
import { Role5120100StatementByDirectorsComponent } from '../role5-120100-statement-by-directors/role5-120100-statement-by-directors.component';
import { Role6120200DirectorBusinessReviewComponent } from '../role6-120200-director-business-review/role6-120200-director-business-review.component';
import { Role7130000AuditorsReportComponent } from '../role7-130000-auditors-report/role7-130000-auditors-report.component';
import { Role8210000StatementOfFinancialPositionComponent } from '../role8-210000-statement-of-financial-position/role8-210000-statement-of-financial-position.component';
import { Role9210100SubClassificationComponent } from '../role9-210100-sub-classification/role9-210100-sub-classification.component';
import { Role10220000StatementOfFinancialPositionLiquidityComponent } from '../role10-220000-statement-of-financial-position-liquidity/role10-220000-statement-of-financial-position-liquidity.component';
import { Role11220100SubClassificationLiquidityComponent } from '../role11-220100-sub-classification-liquidity/role11-220100-sub-classification-liquidity.component';
import { Role12310000StatementOfProfitOrLossComponent } from '../role12-310000-statement-of-profit-or-loss/role12-310000-statement-of-profit-or-loss.component';
import { Role13310100AnalysisOfProfitOrLossComponent } from '../role13-310100-analysis-of-profit-or-loss/role13-310100-analysis-of-profit-or-loss.component';
import { Role14320000StatementOfProfitOrLossByNatureComponent } from '../role14-320000-statement-of-profit-or-loss-by-nature/role14-320000-statement-of-profit-or-loss-by-nature.component';
import { Role15320100AnalysisOfProfitOrLossByNatureComponent } from '../role15-320100-analysis-of-profit-or-loss-by-nature/role15-320100-analysis-of-profit-or-loss-by-nature.component';
import { Role15410000StatementOfComprehensiveIncomeComponent } from '../role15-410000-statement-of-comprehensive-income/role15-410000-statement-of-comprehensive-income.component';
import { Role16420000StatementOfComprehensiveIncomeBeforeTaxComponent } from '../role16-420000-statement-of-comprehensive-income-before-tax/role16-420000-statement-of-comprehensive-income-before-tax.component';
import { Role17510000StatementOfCashFlowsDirectComponent } from '../role17-510000-statement-of-cash-flows-direct/role17-510000-statement-of-cash-flows-direct.component';
import { Role18520000StatementOfCashFlowsIndirectComponent } from '../role18-520000-statement-of-cash-flows-indirect/role18-520000-statement-of-cash-flows-indirect.component';
import { Role19610000StatementOfChangesInEquityComponent } from '../role19-610000-statement-of-changes-in-equity/role19-610000-statement-of-changes-in-equity.component';
import { Role20710000NotesCorporateInformationComponent } from '../role20-710000-notes-corporate-information/role20-710000-notes-corporate-information.component';
import { Role21720000NotesAccountingPoliciesComponent } from '../role21-720000-notes-accounting-policies/role21-720000-notes-accounting-policies.component';
import { Role22730000NotesListOfNotesComponent } from '../role22-730000-notes-list-of-notes/role22-730000-notes-list-of-notes.component';
import { Role23740000NotesIssuedCapitalComponent } from '../role23-740000-notes-issued-capital/role23-740000-notes-issued-capital.component';
import { Role24750000NotesRelatedPartyTransactionsComponent } from '../role24-750000-notes-related-party-transactions/role24-750000-notes-related-party-transactions.component';
import { InputIconModule } from 'primeng/inputicon';
import { SkeletonModule } from 'primeng/skeleton';
import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { FormSubmitService } from '../../../services/mbrs/form-submit.service';
import { InteractiveToastComponent } from '../../common/interactive-toast/interactive-toast.component';
import { FilingLoaderOutletComponent } from '../../../services/mbrs/loaders/filing-loader-outlet.component';
import { XbrlPreviewService } from '../../../services/common/xbrl-preview.service';
import { FILING_LOADER_STRATEGY } from '../../../services/mbrs/loaders/filing-loader.token';
import { FilingFormPersistenceService } from '../../../services/mbrs/filing-form-persistence.service';
import { SUGGESTION_CAPABLE_SHEETS } from '../../../services/mbrs/suggestion-capability';
import { CROSS_SHEET_VALIDATION_DEBOUNCE_MS } from '../../../utils/debounce-times.util';
import {
  TOAST_DURATION_DEFAULT_MS,
  TOAST_DURATION_LONG_MS,
} from '../../../utils/toast-durations.util';
// SheetStatusInitializerService removed — sheet statuses now come from the API

// `SheetType` and `SheetOption` now live in `models/sheet-types.ts` so the
// applicability logic can be shared with `SheetApplicabilityService` without a
// circular dependency on this component.

/**
 * Filing-info gate sheets — the two sheets whose content drives downstream
 * sheet enablement and form-data shape. Leaving either of them with unsaved
 * edits would silently lose data, so navigation away from them is blocked
 * (in `onSheetChange`) and full-route deactivation is gated by
 * `canDeactivateFilingGuard`. role0 ('filing-prerequisites') is the Phase 1
 * surface; role1 ('filing-info') is the Phase 2 surface — exactly one is
 * visible per phase, but the gate logic is identical for both.
 */
export const FILING_INFO_GATES: ReadonlyArray<{
  sheet: SheetType;
  stateKey: keyof FilingFormState;
}> = [
  { sheet: 'filing-prerequisites', stateKey: SHEET_KEYS.filingPrerequisites },
  { sheet: 'filing-info', stateKey: SHEET_KEYS.filingInfo },
];

/**
 * Navigation-state key set on the cross-tab "conversion reset" redirect (a
 * sibling tab cancelled/reverted this conversion). `canDeactivateFilingGuard`
 * honours it to skip the "unsaved changes" leave prompt — the user already
 * confirmed the cancel/revert on the origin tab, so re-prompting the passive
 * tab would be redundant and would trap it on the form. Mirrors the
 * forced-logout `authReason` bypass.
 */
export const CROSS_TAB_RESET_NAV_STATE = 'crossTabReset';

/**
 * Navigation-state key set on the redirect that follows a SUCCESSFUL Phase-2
 * submit from Filing Prerequisites (role0) or Filing Information (role1) — see
 * `onRole0Submit` / `onRole1Submit`. `canDeactivateFilingGuard` honours it to
 * skip the "unsaved changes" leave prompt.
 *
 * Why it is needed: those two submits commit only their OWN sheet
 * (`commitSheetAsOriginal`), unlike Submit for Validation which commits all of
 * them (`commitModifiedStateAsOriginal`). The guard blocks on
 * `hasAnySheetModified()` across all 24 sheets, so any other sheet the user had
 * edited raised "Leaving this page? You have unsaved changes" on a submit that
 * had already succeeded — and "Stay on this page" then cancelled the navigation
 * carrying the "we are converting…" toast, leaving no confirmation at all.
 * The submit is already persisted by the time we navigate, so the prompt can
 * never be useful here.
 */
export const PHASE2_SUBMIT_NAV_STATE = 'phase2Submit';

/**
 * The engine to preselect in the picker on whichever sheet is mounted.
 *
 * One expression serves both sheets, because it degrades in the right order:
 *  - role 0 (a Phase 1 conversion) has no Phase 2 run yet, so this yields the
 *    Phase 1 engine and the user continues with what just ran;
 *  - role 1 (a Phase 2 conversion) yields the engine that produced the data now
 *    on screen, so a re-run repeats it unless the user says otherwise.
 *
 * Null when the backend has recorded neither, which sends the picker to the
 * server's configured default. Module-level and pure so it can be tested
 * without standing up the container's large dependency graph.
 */
export function resolveLastUsedEngine(
  conversion: Pick<ConversionData, 'phase1ConversionEngine' | 'phase2ConversionEngine'> | null,
): string | null {
  return conversion?.phase2ConversionEngine ?? conversion?.phase1ConversionEngine ?? null;
}

/** The filing page's failed-load state; see `FilingFormContainerComponent.loadFailure`. */
export interface FilingLoadFailureState {
  failure: LoadFailureClass;
  /** Exactly what the toast showed — sentence plus diagnostic code. */
  summary: string;
}

/** The scenarios file that decides which sheets a filing hides. Loaded through `fetchJsonAsset`. */
const SCENARIOS_CONFIG_PATH = '/assets/scenarios.config.json';

@Component({
  selector: 'app-filing-form-container',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    DialogModule,
    Select,
    ButtonModule,
    Toast,
    ConfirmDialog,
    InputIconModule,
    MessageModule,
    InteractiveToastComponent,
    FilingLoadFailurePanelComponent,
    Role0000000FilingPrerequisitesComponent,
    Role1010000FilingInfoComponent,
    Role2020000ScopeOfFilingComponent,
    Role3110000StockExchangeComponent,
    Role4120000DirectorsReportComponent,
    Role5120100StatementByDirectorsComponent,
    Role6120200DirectorBusinessReviewComponent,
    Role7130000AuditorsReportComponent,
    Role8210000StatementOfFinancialPositionComponent,
    Role9210100SubClassificationComponent,
    Role10220000StatementOfFinancialPositionLiquidityComponent,
    Role11220100SubClassificationLiquidityComponent,
    Role12310000StatementOfProfitOrLossComponent,
    Role13310100AnalysisOfProfitOrLossComponent,
    Role14320000StatementOfProfitOrLossByNatureComponent,
    Role15320100AnalysisOfProfitOrLossByNatureComponent,
    Role15410000StatementOfComprehensiveIncomeComponent,
    Role16420000StatementOfComprehensiveIncomeBeforeTaxComponent,
    Role17510000StatementOfCashFlowsDirectComponent,
    Role18520000StatementOfCashFlowsIndirectComponent,
    Role19610000StatementOfChangesInEquityComponent,
    Role20710000NotesCorporateInformationComponent,
    Role21720000NotesAccountingPoliciesComponent,
    Role22730000NotesListOfNotesComponent,
    Role23740000NotesIssuedCapitalComponent,
    Role24750000NotesRelatedPartyTransactionsComponent,
    // Still needed for the sheet-status circles in the selector — only the
    // hydration loader stopped using it.
    SkeletonModule,
    SheetLoaderComponent,
    FilingLoaderOutletComponent,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './filing-form-container.component.html',
  styleUrls: ['./filing-form-container.component.css'],
})
export class FilingFormContainerComponent
  implements OnInit, OnDestroy
{
  private stateService = inject(FilingFormStateService);
  private readonly persistence = inject(FilingFormPersistenceService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private sessionGate = inject(SessionGateService);
  private retrieveMbrsDataService = inject(RetrieveMbrsDataService);
  private loadTrace = inject(SheetLoadTraceService);
  private filingModeService = inject(FilingModeService);
  private formSubmitService = inject(FormSubmitService);
  private sheetDependencyService = inject(SheetDependencyService);
  private conversionValidator = inject(ConversionValidatorService);
  private xbrlPreviewService = inject(XbrlPreviewService);
  private authFailureNotice = inject(AuthFailureNoticeService);
  // Caps the FILING_PREVIEW load-failure toast to one per class — see
  // PageLoadFailureToastGate's own docblock.
  private readonly loadFailureToastGate = new PageLoadFailureToastGate(
    undefined,
    // REFACTORED-DIAGNOSTIC-CODES-FLAG: silent page-level toasts in production.
    () => this.runtimeConfig.refactoredDiagnosticCodesEnabled(),
  );
  private population = inject(FilingFormPopulationService);
  private sheetApplicability = inject(SheetApplicabilityService);
  private loadProgress = inject(FILING_LOADER_STRATEGY);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  auditYear = signal<number>(0);

  conversionId = signal<string | null>(null);
  companyName = signal<string>('');
  fileName = signal<string>(''); // Store file name for error messages
  conversionStatus = signal<number | null>(null); // Store actual conversion status for delete dialog
  /**
   * Created/modified audit trail of this conversion, for the delete dialog on
   * roles 0/1. Single source: the `xbrl-data-listing` row (see
   * `applyConversionListingResponse`); `retrieve-mbrs-data` carries no audit
   * fields. `null` until the listing read answers; if it fails, the dialog
   * shows "-".
   *
   * Future rework: if the backend refactors `xbrl-data-listing`, only a change
   * to its field names would touch this signal — see `ConversionAuditTrail`
   * in `src/types/commons.ts`.
   */
  conversionAudit = signal<ConversionAuditTrail | null>(null);
  /**
   * Current conversion phase supplied by the backend. Drives the sheet-list
   * gate in `getEnabledSheets()`: Phase 1 exposes only `filing-prerequisites`
   * and `scope-of-filing` (the only editable sheets before the user clicks
   * "proceed"), Phase 2 exposes the full `typeOfSubmission`-driven list.
   * `null` = not yet known (pre-fetch or pre-rollout); treated as Phase 2 to
   * preserve today's behaviour when the optional `phase` field is missing.
   */
  conversionPhase = signal<ConversionPhaseEnum | null>(null);
  /** Preselection for the engine picker. See `resolveLastUsedEngine`. */
  lastUsedEngine = signal<string | null>(null);
  allSheetsData = signal<MbrsSheetData>({});
  route = inject(ActivatedRoute);
  router = inject(Router);
  private previousUrlTracker = inject(PreviousUrlTrackerService);

  // Navigation blocking dialog state
  isNavigationBlockDialogOpen = signal<boolean>(false);
  pendingNavigationTarget: SheetType | null = null; // Store target sheet when navigation is blocked
  // False until the queryParams handler has applied its FIRST emission (page
  // load). Only LATER `?sheet=` changes (field links) re-check the session;
  // the route guard has just checked it for the load itself.
  private hasAppliedSheetParam = false;

  /**
   * True when the conversion's Phase-2 Filing Information sheet has unsubmitted
   * changes — in THIS tab or, via cross-tab sync, in a sibling. Fed by
   * `watchFilingInfoModified()`, which `ngOnInit` calls.
   *
   * Developer Note (QoL, deferred by P.O. schedule 2026-08-21): the only latency
   * here is `CrossTabSyncService.NOTIFY_DEBOUNCE_MS` (300ms) before a sibling's
   * edit reaches this tab. A `notifyImmediate`-based stream key would remove it,
   * but it is NOT a drop-in replacement: an edge-triggered broadcast fires once
   * and cannot serve a tab opened AFTER the edit, so it would have to be added
   * ALONGSIDE this predicate as an accelerator, never instead of it.
   */
  protected readonly filingInfoModified = signal<boolean>(false);

  /**
   * Drives the cross-tab "Submit Filing Info to Continue" block — an
   * informational, button-less dialog shown in every OTHER tab of this
   * conversion while Filing Information has unsubmitted changes.
   *
   * `currentSheet !== 'filing-info'` does three jobs at once: it excludes the tab
   * doing the editing (no tab-identity logic needed), it implements the P.O.
   * ruling that a SECOND Filing Information tab just syncs rather than blocking,
   * and it keeps this dialog mutually exclusive with the same-tab navigation
   * block (which only fires while ON a gate sheet) by construction.
   *
   * ...but ONLY once `currentSheet` actually means something. On a cold mount it
   * holds its construction-time default `'filing-prerequisites'`, which is a
   * different sheet from the one this tab is about to land on — see
   * `landingSheetResolved`, which gates the whole predicate for exactly that
   * reason.
   */
  readonly isCrossTabFilingInfoBlockOpen = this.buildCrossTabFilingInfoBlock();

  /**
   * True once this tab's INITIAL landing sheet has actually been applied to
   * `currentSheet`. Flipped by `enforcePhaseGatedSheet()` in the branches that
   * settle the sheet themselves, and by the post-hydration restore for the
   * `?sheet=` deep-link branch that defers the decision to it.
   *
   * Why the block predicate cannot run without it (root cause of the "popup
   * flashes on load, opaque backdrop stays behind" defect, 2026-08-21): opening
   * a conversion whose Filing Information a sibling tab has left dirty hits a
   * window where every other term is already satisfied and `currentSheet` is
   * still the default. Sequence inside the `queryParams` handler:
   *
   *   hydrated.set(false) → setConversionId() → await whenHydrated()
   *     ↳ the sibling's dirty filingInfo slice lands, so `filingInfoModified`
   *       is already true (both feeding streams are BehaviorSubjects)
   *   hydrated.set(true)                       ← predicate would open HERE
   *   await persistence.getCurrentSheet(...)   ← an IndexedDB read, then
   *   fetchXbrlPreviewListing(...) resolves    ← an HTTP round trip, before
   *   enforcePhaseGatedSheet() → currentSheet = 'filing-info' → closes again
   *
   * The popup therefore opened on the one tab the `currentSheet` term exists to
   * exclude, and closed a beat later. Whether it was visible at all came down to
   * whether a change-detection tick landed in the gap, which is why it read as
   * intermittent. The close is the damaging half: PrimeNG's `Dialog` renders the
   * modal mask the instant `visible` turns true but only tears it down from
   * `onAnimationEnd({toState:'void'})`, so a flip back to false before the
   * container's enter animation has run leaves `maskVisible` stuck true — an
   * opaque full-screen mask with no dialog in it. (The `@if` wrapper on the
   * `<p-dialog>` in the template is the structural defence for that half.)
   *
   * Fail-safe direction is deliberate: if this never flips, no block is shown.
   * A missing informational popup is a far better failure than a permanent
   * page-blocking backdrop.
   */
  private readonly landingSheetResolved = signal<boolean>(false);

  /**
   * Extracted so the predicate can be exercised against a prototype host in
   * `cross-tab-filing-info-block.spec.ts` without a TestBed.
   */
  private buildCrossTabFilingInfoBlock() {
    return computed(
      () =>
        this.hydrated() &&
        !!this.conversionId() &&
        // Tested POSITIVELY. `conversionPhase` starts null and is resolved
        // asynchronously, and the previous `!== Phase1` phrasing scored that
        // unknown as eligible — one of the two terms that let the cold-mount
        // window above open at all.
        this.conversionPhase() === ConversionPhaseEnum.Phase2 &&
        this.landingSheetResolved() &&
        this.currentSheet !== 'filing-info' &&
        this.filingInfoModified()
    );
  }

  /**
   * Feed `filingInfoModified` from the dual-state store.
   *
   * Cross-tab Filing Information block. Both streams are watched because
   * `isSheetModified` is a diff of modified-vs-original: a sibling's EDIT moves
   * the modified slice, and a submit/revert moves the original one. Watching
   * only one leaves the block stuck open or stuck shut. Emissions arrive here
   * through the same cross-tab rehydrate that already updates this tab's data.
   *
   * Extracted from `ngOnInit` for the same reason
   * `buildCrossTabFilingInfoBlock()` was: it can then be exercised against a
   * prototype host in `cross-tab-filing-info-block.spec.ts` without a TestBed,
   * so dropping either stream fails a spec instead of shipping green.
   */
  private watchFilingInfoModified(): void {
    merge(this.stateService.state$, this.stateService.originalState$)
      .pipe(
        map(() => this.stateService.isSheetModified(SHEET_KEYS.filingInfo)),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((modified) => this.filingInfoModified.set(modified));
  }

  // Leave-page confirmation dialog state (for route deactivation guard)
  isLeavePageDialogOpen = signal<boolean>(false);
  private leavePageResolver: ((value: boolean) => void) | null = null;

  // Pre-validation complete flag (set immediately since API provides statuses)
  preValidationComplete = signal<boolean>(false);

  /**
   * Set when the conversion-wide load failed with any class except PERM/RESP/AUTH
   * (P.O., 2026-10-05: stay on the page instead of redirecting). While set,
   * the template renders `FilingLoadFailurePanelComponent` instead of any
   * sheet, so nothing can be edited, submitted or persisted, and the sheet
   * selector is disabled. A fresh container (see `retryLoad`) starts null.
   */
  loadFailure = signal<FilingLoadFailureState | null>(null);

  /**
   * Set when ONE sheet's own load ended in a terminal failure (ASSET / PARSE /
   * NET / BUILD) while the conversion loaded fine. The template swaps just that
   * sheet's body for `FilingLoadFailurePanelComponent`; the selector stays
   * enabled (unlike {@link loadFailure}) so every other sheet remains reachable.
   * Cleared when the user changes sheet or retries. `STALL` never sets it — a
   * stalled sheet may still finish — and a degraded `warn` (MSIC) never does
   * either: both stay toast-only.
   */
  readonly sheetLoadFailure = signal<{ scope: string; summary: string } | null>(null);
  protected readonly runtimeConfig = inject(RuntimeConfigService);

  // Hydration mount-gate. State persistence is async (IndexedDB); this flips
  // true only after `FilingFormStateService.whenHydrated()` resolves for the
  // active conversion. The template renders NO sheet component until then, so
  // a sheet's on-mount setTimeout(100→600) "read form + persist" chain can
  // never run before hydration lands and overwrite real state with schema
  // defaults. See ngOnInit query-params flow where it is driven.
  protected readonly hydrated = signal<boolean>(false);

  /**
   * True when a sheet body may mount. Beyond IndexedDB hydration, Filing
   * Prerequisites (the construction-time default sheet) waits for the phase to
   * be known and to be Phase 1: role 0 is hidden in Phase 2, but the phase
   * arrives from the network AFTER the first mount, so mounting the default
   * sheet blindly opened a role-0 load trace (and its 15s watchdog) on every
   * Phase-2 filing — a wrong `S0` code, reported twice, before Filing
   * Information even mounted with a fresh watchdog of its own.
   */
  protected readonly sheetBodyMountable = computed(
    () =>
      this.hydrated() &&
      (this.currentSheet !== 'filing-prerequisites' ||
        this.conversionPhase() === ConversionPhaseEnum.Phase1 ||
        // Phase never reported (pre-rollout backends fall through to Phase 1
        // defaults): once the load has settled, stop waiting for it.
        (this.conversionPhase() === null && this.mbrsLoadSettled()))
  );

  /** True once the MBRS-data load (or its in-session replay) has finished, success or not. */
  private readonly mbrsLoadSettled = signal<boolean>(false);

  // Background validation: cycles through each sheet to run its component validation
  // null = not currently validating any sheet in background
  private validationCycleCompleted = false;

  /**
   * Backing signal for `currentSheet`. The public getter/setter below keep the
   * field's original name and type, so every existing read and write — template
   * included — is unchanged; the signal exists so `isCrossTabFilingInfoBlockOpen`
   * can track sheet changes in a `computed()`. A plain field cannot be tracked.
   */
  private readonly currentSheetSignal = signal<SheetType>('filing-prerequisites');

  get currentSheet(): SheetType {
    return this.currentSheetSignal();
  }

  set currentSheet(sheet: SheetType) {
    // A failure panel belongs to the sheet it was raised for. `?.` because specs
    // drive this class through a prototype host that never ran field initialisers.
    if (sheet !== this.currentSheetSignal()) {
      this.sheetLoadFailure?.set(null);
    }
    this.currentSheetSignal.set(sheet);
  }

  /**
   * A sheet-origin failure notice → the per-sheet panel. Only a terminal failure
   * of the sheet currently shown counts; see {@link sheetLoadFailure}.
   */
  private onLoadFailureNotice(notice: LoadFailureNotice): void {
    if (notice.origin !== 'sheet' || !notice.terminal || notice.failure === 'STALL') return;
    if (notice.scope !== filingSheetScope(this.currentSheet)) return;
    this.sheetLoadFailure.set({ scope: notice.scope, summary: notice.summary });
  }

  /** Retry ONE failed sheet: clearing the panel remounts the sheet body, which reloads it. */
  retrySheetLoad(): void {
    this.sheetLoadFailure.set(null);
  }

  /**
   * The scope handed to every `sessionGate.confirmSessionBeforeLeaving()` call
   * here. The gate runs BEFORE the switch, so `currentSheet` is still the sheet
   * being LEFT and the AUTH code reads `MBRS-S<role>-AUTH-<conversion>`.
   */
  private currentSheetDiagnosticScope(): DiagnosticScope {
    return { scope: filingSheetScope(this.currentSheet), conversionId: this.conversionId() };
  }

  // True until enforcePhaseGatedSheet() resolves the phase for the first time
  // on this component instance. The component re-instantiates on every entry
  // to /mbrs/filing (refresh, tab reopen, in-app navigation from another
  // route), so this flag fires on every entry — Phase 2 always lands on
  // 'filing-info' and Phase 1 always lands on 'filing-prerequisites',
  // regardless of the sheet stored in localStorage. In-session sheet
  // switching uses onSheetChange/prev/next, which do not pass through
  // enforcePhaseGatedSheet, so this snap does not interfere with normal
  // navigation.
  private isInitialPhaseResolution = true;

  // Filtered sheets based on Role-1 method selections
  filteredSheets: SheetOption[] = [];

  // Scenarios configuration loaded from JSON. Set only on a successful, readable
  // load: a failure is FATAL (see `loadScenariosConfig`), never an empty fallback.
  private scenariosConfig: any = null;

  /**
   * Settles when `scenarios.config.json` has loaded (resolves) or failed
   * (rejects with a `SchemaAssetError`). Both load paths wait on it before they
   * fetch or replay, because the scenarios decide which sheets a filing hides.
   * Read it through {@link awaitScenarios}.
   */
  private scenariosReady: Promise<void> | undefined;

  // Subscription for reactive company name updates
  private companyNameSubscription?: Subscription;

  // Subscription for Role-1 method field changes
  private methodFieldsSubscription?: Subscription;

  // Guard so setupActiveFilingInfoSubscriptions() runs exactly once per
  // component lifetime — it can be called from the queryParams listing
  // response, from fetchAllMbrsData, or eagerly when there's no conversionId.
  private activeFilingInfoSubscriptionsReady = false;

  // Subscription for state changes to update status indicators
  private stateChangeSubscription?: Subscription;
  private validationSubscription?: Subscription;
  private pendingValidationSubscription?: Subscription;
  private crossSheetVerdictSubscription?: Subscription;

  // Flag to suppress excessive cdr.detectChanges() during background validation cycle
  private isRunningValidationCycle = false;

  // Signal to track state version for reactive status indicator updates
  stateVersion = signal<number>(0);

  // Pre-computed sheet status cache — refreshed once per stateVersion change
  // Eliminates ~168 getSheetStatus() calls per change detection cycle
  sheetStatusCache = new Map<string, { status: 'valid' | 'invalid' | 'edited' | 'empty'; hasSuggested: boolean }>();

  // ViewChild reference to the sheet selector dropdown for manual control
  @ViewChild('sheetSelector') sheetSelector?: Select;

  // ViewChild references for all role components to access their autoFillDemo() methods
  @ViewChild(Role0000000FilingPrerequisitesComponent)
  role0Component?: Role0000000FilingPrerequisitesComponent;
  @ViewChild(Role1010000FilingInfoComponent)
  role1Component?: Role1010000FilingInfoComponent;
  @ViewChild(Role2020000ScopeOfFilingComponent)
  role2Component?: Role2020000ScopeOfFilingComponent;
  @ViewChild(Role3110000StockExchangeComponent)
  role3Component?: Role3110000StockExchangeComponent;
  @ViewChild(Role4120000DirectorsReportComponent)
  role4Component?: Role4120000DirectorsReportComponent;
  @ViewChild(Role5120100StatementByDirectorsComponent)
  role5Component?: Role5120100StatementByDirectorsComponent;
  @ViewChild(Role6120200DirectorBusinessReviewComponent)
  role6Component?: Role6120200DirectorBusinessReviewComponent;
  @ViewChild(Role7130000AuditorsReportComponent)
  role7Component?: Role7130000AuditorsReportComponent;
  @ViewChild(Role8210000StatementOfFinancialPositionComponent)
  role8Component?: Role8210000StatementOfFinancialPositionComponent;
  @ViewChild(Role9210100SubClassificationComponent)
  role9Component?: Role9210100SubClassificationComponent;
  @ViewChild(Role10220000StatementOfFinancialPositionLiquidityComponent)
  role10Component?: Role10220000StatementOfFinancialPositionLiquidityComponent;
  @ViewChild(Role11220100SubClassificationLiquidityComponent)
  role11Component?: Role11220100SubClassificationLiquidityComponent;
  @ViewChild(Role12310000StatementOfProfitOrLossComponent)
  role12Component?: Role12310000StatementOfProfitOrLossComponent;
  @ViewChild(Role13310100AnalysisOfProfitOrLossComponent)
  role13Component?: Role13310100AnalysisOfProfitOrLossComponent;
  @ViewChild(Role14320000StatementOfProfitOrLossByNatureComponent)
  role14Component?: Role14320000StatementOfProfitOrLossByNatureComponent;
  @ViewChild(Role15320100AnalysisOfProfitOrLossByNatureComponent)
  role15bComponent?: Role15320100AnalysisOfProfitOrLossByNatureComponent;
  @ViewChild(Role15410000StatementOfComprehensiveIncomeComponent)
  role15Component?: Role15410000StatementOfComprehensiveIncomeComponent;
  @ViewChild(Role16420000StatementOfComprehensiveIncomeBeforeTaxComponent)
  role16Component?: Role16420000StatementOfComprehensiveIncomeBeforeTaxComponent;
  @ViewChild(Role17510000StatementOfCashFlowsDirectComponent)
  role17Component?: Role17510000StatementOfCashFlowsDirectComponent;
  @ViewChild(Role18520000StatementOfCashFlowsIndirectComponent)
  role18Component?: Role18520000StatementOfCashFlowsIndirectComponent;
  @ViewChild(Role19610000StatementOfChangesInEquityComponent)
  role19Component?: Role19610000StatementOfChangesInEquityComponent;
  @ViewChild(Role20710000NotesCorporateInformationComponent)
  role20Component?: Role20710000NotesCorporateInformationComponent;
  @ViewChild(Role21720000NotesAccountingPoliciesComponent)
  role21Component?: Role21720000NotesAccountingPoliciesComponent;
  @ViewChild(Role22730000NotesListOfNotesComponent)
  role22Component?: Role22730000NotesListOfNotesComponent;
  @ViewChild(Role23740000NotesIssuedCapitalComponent)
  role23Component?: Role23740000NotesIssuedCapitalComponent;
  @ViewChild(Role24750000NotesRelatedPartyTransactionsComponent)
  role24Component?: Role24750000NotesRelatedPartyTransactionsComponent;

  /**
   * The sheet selector's contents. This array's ORDER is the only thing that
   * defines the selector sequence, prev/next traversal, and the "sheet X of Y"
   * counter — see the nested `xbrl-form/CLAUDE.md` before reordering anything.
   *
   * Each `label` reads from `SHEET_DISPLAY_NAMES` rather than repeating the name
   * inline, because the field-link tooltips on the Statement of Financial
   * Position and Statement of Profit or Loss now name the destination sheet too
   * ("Open … in Sub-classification of Assets, Liabilities & Equity"), and two
   * copies of a sheet's name would drift the moment one is reworded. The map is
   * a lookup only — it carries no ordering.
   */
  sheets: SheetOption[] = [
    {
      label: SHEET_DISPLAY_NAMES['filing-prerequisites'],
      value: 'filing-prerequisites',
      icon: 'pi pi-cog',
      description: '[000000] Filing Prerequisites & Configuration',
    },
    // Role-1 (Filing Information) is the Phase-2 editable filing-info surface.
    // The phase-aware filter at the end of getEnabledSheets() surfaces
    // exactly one of filing-prerequisites (Phase 1) or filing-info (Phase 2)
    // at a time, so both carry the same user-facing "Filing Information"
    // label without collision.
    {
      label: SHEET_DISPLAY_NAMES['filing-info'],
      value: 'filing-info',
      icon: 'pi pi-cog',
      description: '[010000] Filing Information',
    },
    {
      label: SHEET_DISPLAY_NAMES['scope-of-filing'],
      value: 'scope-of-filing',
      icon: 'pi pi-building',
      description: '[020000] Scope of filing',
    },
    {
      label: SHEET_DISPLAY_NAMES['directors-report'],
      value: 'directors-report',
      icon: 'pi pi-users',
      description: '[120000] Disclosure - Directors report',
    },
    // ORDER IS DELIBERATE — it mirrors the SSM / government MBRS Prep Tool's own
    // sheet sequence, which preparers work from. That is NOT the same as
    // ascending MBRS role-code order: Stock Exchange [110000] deliberately sits
    // AFTER the whole [120000] disclosure block (see below). Do not "tidy" the
    // array into numeric order.
    //
    // Role-5 [120100] Statement by Directors comes BEFORE Role-6 [120200]
    // Director Business Review. This pair has been flipped the wrong way twice;
    // if you are about to reorder it, check the Prep Tool first rather than
    // inferring the intent from the role codes.
    //
    // Consequence: this array's order intentionally differs from
    // SHEET_DESCRIPTORS, SheetApplicabilityService's coreSheetValues /
    // computeRequiredSheets, and conversion-validator's ALL_SHEET_KEYS. Those
    // four are maps / membership sets where order carries no meaning, so the
    // divergence is harmless — only THIS array's order is user-visible (it
    // drives the selector, prev/next, and the "sheet X of Y" counter). The
    // `## Sheets` lists under docs/specifications/permutations/ mirror it.
    {
      label: SHEET_DISPLAY_NAMES['statement-by-directors'],
      value: 'statement-by-directors',
      icon: 'pi pi-file-check',
      description: '[120100] Disclosure - Statement by directors',
    },
    {
      label: SHEET_DISPLAY_NAMES['director-business-review'],
      value: 'director-business-review',
      icon: 'pi pi-chart-bar',
      description: '[120200] Disclosure - Director business review',
    },
    {
      label: SHEET_DISPLAY_NAMES['stock-exchange'],
      value: 'stock-exchange',
      icon: 'pi pi-chart-line',
      description: '[110000] Disclosure - Involvement in Stock Exchange',
    },
    {
      label: SHEET_DISPLAY_NAMES['auditors-report'],
      value: 'auditors-report',
      icon: 'pi pi-verified',
      description: '[130000] Disclosure - Auditors report to members',
    },
    {
      label: SHEET_DISPLAY_NAMES['statement-of-financial-position'],
      value: 'statement-of-financial-position',
      icon: 'pi pi-money-bill',
      description:
        '[210000] Statement of financial position, current/non-current',
    },
    {
      label: SHEET_DISPLAY_NAMES['sub-classification'],
      value: 'sub-classification',
      icon: 'pi pi-list',
      description: '[210100] Sub-classification by current/non-current method',
    },
    {
      label: SHEET_DISPLAY_NAMES['statement-of-financial-position-liquidity'],
      value: 'statement-of-financial-position-liquidity',
      icon: 'pi pi-chart-line',
      description:
        '[220000] Statement of financial position by order of liquidity',
    },
    {
      label: SHEET_DISPLAY_NAMES['sub-classification-liquidity'],
      value: 'sub-classification-liquidity',
      icon: 'pi pi-list',
      description: '[220100] Sub-classification by order of liquidity method',
    },
    {
      label: SHEET_DISPLAY_NAMES['statement-of-profit-or-loss'],
      value: 'statement-of-profit-or-loss',
      icon: 'pi pi-dollar',
      description:
        '[310000] Statement of profit or loss, by function of expense',
    },
    {
      label: SHEET_DISPLAY_NAMES['analysis-of-profit-or-loss'],
      value: 'analysis-of-profit-or-loss',
      icon: 'pi pi-chart-bar',
      description:
        '[310100] Analysis of profit or loss, by function of expense',
    },
    {
      label: SHEET_DISPLAY_NAMES['statement-of-profit-or-loss-by-nature'],
      value: 'statement-of-profit-or-loss-by-nature',
      icon: 'pi pi-leaf',
      description: '[320000] Statement of profit or loss, by nature of expense',
    },
    {
      label: SHEET_DISPLAY_NAMES['analysis-of-profit-or-loss-by-nature'],
      value: 'analysis-of-profit-or-loss-by-nature',
      icon: 'pi pi-chart-bar',
      description: '[320100] Analysis of profit or loss, by nature of expense',
    },
    {
      label: SHEET_DISPLAY_NAMES['statement-of-comprehensive-income'],
      value: 'statement-of-comprehensive-income',
      icon: 'pi pi-th-large',
      description: '[410000] Statement of comprehensive income, net of tax',
    },
    {
      label: SHEET_DISPLAY_NAMES['statement-of-comprehensive-income-before-tax'],
      value: 'statement-of-comprehensive-income-before-tax',
      icon: 'pi pi-table',
      description: '[420000] Statement of comprehensive income, before tax',
    },
    {
      label: SHEET_DISPLAY_NAMES['statement-of-cash-flows-direct'],
      value: 'statement-of-cash-flows-direct',
      icon: 'pi pi-directions',
      description: '[510000] Statement of cash flows, direct method',
    },
    {
      label: SHEET_DISPLAY_NAMES['statement-of-cash-flows-indirect'],
      value: 'statement-of-cash-flows-indirect',
      icon: 'pi pi-replay',
      description: '[520000] Statement of cash flows, indirect method',
    },
    {
      label: SHEET_DISPLAY_NAMES['statement-of-changes-in-equity'],
      value: 'statement-of-changes-in-equity',
      icon: 'pi pi-arrows-h',
      description: '[610000] Statement of changes in equity',
    },
    {
      label: SHEET_DISPLAY_NAMES['notes-corporate-information'],
      value: 'notes-corporate-information',
      icon: 'pi pi-book',
      description: '[710000] Disclosure of corporate information',
    },
    {
      label: SHEET_DISPLAY_NAMES['notes-accounting-policies'],
      value: 'notes-accounting-policies',
      icon: 'pi pi-file-edit',
      description: '[720000] Summary of material accounting policies',
    },
    {
      label: SHEET_DISPLAY_NAMES['notes-list-of-notes'],
      value: 'notes-list-of-notes',
      icon: 'pi pi-list',
      description:
        '[730000] Structured list of all notes to financial statements',
    },
    {
      label: SHEET_DISPLAY_NAMES['notes-issued-capital'],
      value: 'notes-issued-capital',
      icon: 'pi pi-money-bill',
      description: '[740000] Disclosure of issued capital and share movements',
    },
    {
      label: SHEET_DISPLAY_NAMES['notes-related-party-transactions'],
      value: 'notes-related-party-transactions',
      icon: 'pi pi-users',
      description:
        '[750000] Disclosure of related party relationships and transactions',
    },
    // Add more sheets here as they are created:
    // { label: '🔢 Notes - Judgements and Estimates', value: 'notes-judgements-estimates', ... }
  ];

  /** Map SheetType slug → FilingFormState camelCase key (shared across status methods) */
  private readonly sheetTypeToStateKey: { [key in SheetType]: keyof FilingFormState } = {
    'filing-prerequisites': SHEET_KEYS.filingPrerequisites,
    'filing-info': SHEET_KEYS.filingInfo,
    'scope-of-filing': SHEET_KEYS.scopeOfFiling,
    'stock-exchange': SHEET_KEYS.stockExchangeDisclosure,
    'directors-report': SHEET_KEYS.directorsReport,
    'statement-by-directors': SHEET_KEYS.statementByDirectors,
    'director-business-review': SHEET_KEYS.directorBusinessReview,
    'auditors-report': SHEET_KEYS.auditorsReport,
    'statement-of-financial-position': SHEET_KEYS.statementOfFinancialPosition,
    'sub-classification': SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity,
    'statement-of-financial-position-liquidity': SHEET_KEYS.statementOfFinancialPositionLiquidity,
    'sub-classification-liquidity': SHEET_KEYS.subClassificationLiquidity,
    'statement-of-profit-or-loss': SHEET_KEYS.statementOfProfitOrLoss,
    'analysis-of-profit-or-loss': SHEET_KEYS.analysisOfProfitOrLoss,
    'statement-of-profit-or-loss-by-nature': SHEET_KEYS.statementOfProfitOrLossByNature,
    'analysis-of-profit-or-loss-by-nature': SHEET_KEYS.analysisOfProfitOrLossByNature,
    'statement-of-comprehensive-income': SHEET_KEYS.statementOfComprehensiveIncome,
    'statement-of-comprehensive-income-before-tax': SHEET_KEYS.statementOfComprehensiveIncomeBeforeTax,
    'statement-of-cash-flows-direct': SHEET_KEYS.statementOfCashFlowsDirectMethod,
    'statement-of-cash-flows-indirect': SHEET_KEYS.statementOfCashFlowsIndirectMethod,
    'statement-of-changes-in-equity': SHEET_KEYS.statementOfChangesInEquity,
    'notes-corporate-information': SHEET_KEYS.notesCorporateInformation,
    'notes-accounting-policies': SHEET_KEYS.notesAccountingPolicies,
    'notes-list-of-notes': SHEET_KEYS.notesListOfNotes,
    'notes-issued-capital': SHEET_KEYS.notesIssuedCapital,
    'notes-related-party-transactions': SHEET_KEYS.notesRelatedPartyTransactions,
  };

  ngOnInit(): void {
    // Per-sheet abandonment: tell the state layer which sheet this tab has on
    // screen, so it can answer a sibling's `sheetReleased` broadcast. Registered
    // here and withdrawn in ngOnDestroy/onPageHide, which is what makes "no
    // container mounted" mean "this tab vouches for nothing" structurally rather
    // than by predicate. See FilingFormStateService.registerMountedSheetProbe.
    this.stateService.registerMountedSheetProbe(() =>
      this.currentSheet
        ? (this.sheetTypeToStateKey[this.currentSheet] ?? null)
        : null
    );

    // Load scenarios configuration from JSON. Fatal on failure: the data fetch and
    // the navigate-back replay both wait for it (`awaitScenarios`).
    this.scenariosReady = this.loadScenariosConfig();
    // A rejection is consumed by the load paths; this only stops an unobserved
    // copy (component destroyed before either path ran) from being reported as an
    // unhandled rejection.
    this.scenariosReady.catch(() => undefined);

    // One sheet's own terminal load failure puts the inline panel over that sheet
    // (toast stays in the shell). Subscribed before any sheet can mount.
    this.loadTrace.failures$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((notice) => this.onLoadFailureNotice(notice));

    // Cross-tab leave-the-form safeguard: when ANOTHER tab on the same
    // conversion cancels/reverts a sheet (clearing the shared persisted
    // original/modified state) OR resubmits the conversion for validation
    // (committing it back into backend validation), it leaves the form. If this
    // tab is still sitting on a sheet it would otherwise re-read an emptied/stale
    // original and flip every sheet to the pencil icon — or keep editing data the
    // backend is already revalidating. So when the remote reset matches our
    // current conversion, navigate to the listing. (The origin tab is excluded by
    // the cross-tab self-echo filter, so it never redirects itself.)
    this.stateService.conversionResetRequested$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((conversionId) => {
        if (conversionId === this.conversionId()) {
          // Tag the navigation so canDeactivateFilingGuard skips the "unsaved
          // changes" prompt — the cancel/revert was already confirmed on the
          // origin tab, so this passive tab must not be trapped behind a dialog.
          this.router.navigate(['/mbrs/xbrl-files'], {
            state: { [CROSS_TAB_RESET_NAV_STATE]: true },
          });
        }
      });

    this.watchFilingInfoModified();

    // CRITICAL: Check if we have a conversionId from URL params early.
    // If we do, flag MBRS-data hydration as in progress BEFORE calculating
    // filteredSheets so the dropdown is blocked until the API check
    // completes. `beginMbrsHydration()` is idempotent — fetchAllMbrsData()
    // will call it again when the actual fetch kicks off; the matching
    // `endMbrsHydration()` fires from the fetch's finalize / success
    // callbacks. Role components gate their reveal on this flag.
    const urlParams = new URLSearchParams(window.location.search);
    const hasConversionId = !!urlParams.get('id');
    if (hasConversionId) {
      this.stateService.beginMbrsHydration();
    }

    // Note: Saved sheet restoration is deferred to queryParams subscription
    // where conversionId is available for the correct localStorage key.

    // Get initial company name from whichever filing-info slot is populated.
    // We can't yet ask getActiveFilingInfoKey() for the right one because the
    // conversion phase hasn't been resolved at this point in ngOnInit (it
    // arrives via the queryParams listing call below or via fetchAllMbrsData).
    // Both slots are mutually exclusive per phase, so OR-ing them is safe and
    // covers Phase 1 / Phase 2 / unknown alike.
    const initialState = this.stateService.getState();
    const savedCompanyName =
      initialState.filingPrerequisites['ssmt-dei:NameOfReportingEntity'] ||
      initialState.filingInfo['ssmt-dei:NameOfReportingEntity'];

    // Set initial company name from localStorage (if exists)
    if (savedCompanyName) {
      this.companyName.set(savedCompanyName);
    }

    // Initialize filtered sheets (getEnabledSheets() already routes through
    // getActiveFilingInfoData(), so it's phase-correct on its own).
    this.filteredSheets = this.getEnabledSheets();

    // Validate current sheet on initialization
    // (validateCurrentSheet internally skips when stateService.isHydratingMbrsData() is true)
    this.validateCurrentSheet();

    // Defer the company-name and method-fields subscriptions until the
    // conversion phase is known — they need to read from filingPrerequisites
    // (Phase 1) or filingInfo (Phase 2), and binding to the wrong slot in
    // Phase 2 would silently freeze the title bar and getEnabledSheets()
    // against an empty role0 (the inactive slot is intentionally left unfilled
    // by the hydration path; see comments around line 2820). When there is no
    // conversionId we are creating a fresh form, which is Phase 1 by
    // definition, so set up immediately. Otherwise let the listing response
    // (queryParams handler) or fetchAllMbrsData call setupActiveFilingInfoSubscriptions().
    if (!hasConversionId) {
      this.setupActiveFilingInfoSubscriptions();
    }

    // Handle query parameters (only override if localStorage doesn't have company name)
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (params) => {
      // Only use query param if we don't have a saved company name
      if (!savedCompanyName && params['companyName']) {
        this.companyName.set(params['companyName']);
      }

      // Set fallback if no company name from any source
      if (!this.companyName()) {
        this.companyName.set('');
      }

      const conversionId = params['id'] || null;
      this.conversionId.set(conversionId);

      // CRITICAL: Set conversionId in state service for localStorage scoping.
      // This is synchronous (sets the id immediately) and kicks off async
      // hydration of this conversion's state from IndexedDB.
      //
      // Mount-gate: close it before kicking off hydration, then re-open it once
      // hydration resolves. While closed the template shows a loader instead of
      // any sheet component, so no sheet mounts (and runs its on-mount persist
      // chain) before real state has landed. For the early-return cases
      // (`'new'` first-time filing, or a same-id SPA revisit) `setConversionId`
      // no-ops and `whenHydrated()` returns an already-resolved promise, so the
      // gate re-opens on the very next microtask — no visible/infinite loader.
      this.hydrated.set(false);
      // The ONE stall clock for this entry (IndexedDB hydration → conversion
      // fetch → landing sheet finalized). Opened before hydration so a hang in
      // the earliest stage is covered too; see `beginEntryLoad`.
      this.loadTrace.beginEntryLoad(conversionId, () => this.resolveLoadFailureSheetScope());
      this.stateService.setConversionId(conversionId);

      // Update FilingModeService to propagate mode to all role components
      this.filingModeService.setConversionId(conversionId);

      // Wait for IndexedDB hydration to resolve before reading any
      // persisted filing state or restoring the saved current-sheet pointer.
      await this.stateService.whenHydrated();
      this.hydrated.set(true);

      // Restore last active sheet from IndexedDB (now that conversionId is set
      // so the persistence key is correctly scoped, and hydration has resolved).
      const savedSheet = await this.persistence.getCurrentSheet(this.conversionId() ?? '');
      if (savedSheet && this.isValidSheet(savedSheet)) {
        this.currentSheet = savedSheet as SheetType;
      }

      // Fetch conversion data to get fileName and conversion status.
      //
      // Phase is NOT read from this listing response — the authoritative
      // source for conversion phase is the retrieve-mbrs-data endpoint (see
      // fetchAllMbrsData). We still defensively set `conversionPhase` here
      // only when the listing actually carries a value AND the signal is
      // still null, so that a retrieve-mbrs-data response arriving first
      // does not get clobbered by a late-arriving listing response that
      // happens to omit phase.
      if (conversionId) {
        this.xbrlPreviewService
          .fetchXbrlPreviewListing({ conversionId })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (conversionData) => this.applyConversionListingResponse(conversionData),
            // This used to be unreachable: XbrlPreviewService swallowed every
            // transport failure into a resolved stub, so `error` never fired
            // here — the only visible symptom was `httpErrorInterceptor`'s
            // global redirect to the static /error page, which also tore
            // down the mounted sheet before ITS OWN schema-load NET toast
            // could render. Both are fixed at the shared root: the service
            // now propagates, and this classifies + toasts like every other
            // page-load-failure.util.ts consumer. `fileName`/`conversionStatus`
            // simply keep whatever they held before — a missed preview read
            // does not block the rest of the filing form.
            error: (error: unknown) => this.handleXbrlPreviewFailure(error),
          });
      }

      const financialYear = params['auditYear'];
      if (financialYear) {
        this.auditYear.set(parseInt(financialYear, 10));
      }

      // CRITICAL: Handle sheet navigation via query parameter
      // This enables cross-role navigation (e.g., Role-8 → Role-9)
      await this.applySheetFromQueryParam(params);

      // Seed-on-first-entry: when the user lands here from xbrl-files (no
      // ?sheet= param) and no prior selection was persisted for this
      // conversionId, write the resolved currentSheet so the cache reflects
      // reality from the first paint. Guarded by a presence check so we
      // never overwrite an existing saved selection — that invariant is
      // owned by the explicit-action paths (onSheetChange, previousSheet,
      // nextSheet, phase enforcement). See commit 0a35900 for context.
      const existingSavedSheet = await this.persistence.getCurrentSheet(this.conversionId() ?? '');
      if (!existingSavedSheet) {
        this.persistence.putCurrentSheet(this.conversionId() ?? '', this.currentSheet);
      }
    });

    // Fetch all MBRS data from backend (also applies sheet statuses from API).
    //
    // NAVIGATE-BACK PRESERVATION: If this conversion's data has already been
    // loaded during the current JS context, skip the re-fetch. Otherwise
    // loadFromAPI() would clobber the in-memory modified cache and erase the
    // user's unsubmitted edits. The session flag is reset on page refresh
    // (fresh JS context) and on successful submit (via
    // commitModifiedStateAsOriginal -> clearSessionLoadedFlag) so both paths
    // correctly re-fetch fresh backend data.
    //
    // ORDERING: sequence this AFTER whenHydrated() so the conversion's persisted
    // IndexedDB state is restored into the subjects BEFORE loadFromAPI overwrites
    // with backend data. setConversionId() (called synchronously in the
    // queryParams subscriber above, which ActivatedRoute emits synchronously on
    // subscribe) has already kicked off hydration, so whenHydrated() here returns
    // that in-flight promise — resolving once the IDB read settles, or immediately
    // for the 'new'/same-id early-return cases. finally() so the load still runs
    // even if hydration rejects. Previously this ran concurrently with hydration
    // and only worked because the IDB read is faster than the HTTP fetch.
    const currentConversionId = this.conversionId();
    void this.stateService.whenHydrated().finally(() => {
      if (this.stateService.isDataLoadedInCurrentSession(currentConversionId)) {
        this.replayPostLoadUiWiring();
      } else {
        this.fetchAllMbrsData();
      }
    });

    // Subscribe to edited status changes to trigger status indicator re-renders
    this.stateChangeSubscription = this.stateService.edited$.subscribe({
      next: (editedStatus) => {
        // Increment stateVersion and trigger change detection for status indicators
        this.stateVersion.update((version) => version + 1);
        // Force filteredSheets array reference change to trigger PrimeNG Select re-render
        this.filteredSheets = [...this.filteredSheets];
        this.refreshSheetStatusCache();
        if (!this.isRunningValidationCycle && !this.stateService.isHydratingMbrsData()) {
          this.cdr.detectChanges();
        }
      },
      error: (err) => console.error('Error in edited$ subscription:', err)
    });

    // Subscribe to sheet-level status changes to recalculate enabled sheets and update dropdown icons
    // CRITICAL: When filingPrerequisites status changes, we need to recalculate which sheets are enabled
    this.validationSubscription = this.stateService.sheetLevelStatus$.subscribe({
      next: () => {
        this.stateVersion.update((version) => version + 1);
        this.filteredSheets = this.getEnabledSheets();
        this.refreshSheetStatusCache();
        if (!this.isRunningValidationCycle && !this.stateService.isHydratingMbrsData()) {
          this.cdr.detectChanges();
        }
      },
      error: (err) => console.error('Error in sheetLevelStatus$ subscription:', err)
    });

    // Subscribe to pending validation changes to update status icons (show/hide)
    this.pendingValidationSubscription = this.stateService.pendingValidation$.subscribe({
      next: () => {
        this.stateVersion.update((version) => version + 1);
        this.filteredSheets = [...this.filteredSheets];
        this.refreshSheetStatusCache();
        if (!this.isRunningValidationCycle && !this.stateService.isHydratingMbrsData()) {
          this.cdr.detectChanges();
        }
      },
    });

    // Cross-sheet verdict refresh.
    //
    // A pure-verdict sheet's status can change because an UPSTREAM sheet it
    // cross-reads changed (Role 8 SFP ⇐ Role 9 sub-classification; Role 12/15/19
    // ⇐ their feeders; etc.). The dependent sheet's own component only
    // re-publishes while it is mounted AND unlocked, but the background
    // validation cycle locks every sheet and `onSheetChange` unlocks only the
    // one the user navigates to. So editing the active sheet could not flip a
    // different (locked, unmounted) sheet's dropdown icon — it stayed frozen at
    // its load-time value.
    //
    // recomputePureVerdictStatuses() re-runs ONLY the registered pure validators
    // (which re-inherit their upstream sheets themselves) over current state and
    // republishes the rolled-up verdicts, bypassing the lock (a fresh recompute
    // is authoritative — the lock only guards stale orphaned-callback writes).
    // This is the GENERAL mechanism: it covers every PURE_VERDICT_SHEETS member,
    // not just Role 8, so each new cross-sheet dependency is handled the moment
    // both sheets are allow-listed — no per-pair wiring. The resulting
    // sheetLevelStatus$ emission drives the existing cache refresh above.
    //
    // Debounced + guarded so it never fights the one-shot background validation
    // cycle or fires mid-hydration; a settled edit that touches no pure-verdict
    // input is a cheap no-op (recompute publishes only on an actual change).
    this.crossSheetVerdictSubscription = this.stateService.modifiedState$
      .pipe(debounceTime(CROSS_SHEET_VALIDATION_DEBOUNCE_MS))
      .subscribe({
        next: () => {
          if (
            !this.validationCycleCompleted ||
            this.isRunningValidationCycle ||
            this.stateService.isHydratingMbrsData()
          ) {
            return;
          }
          this.stateService.recomputePureVerdictStatuses();
        },
        error: (err) =>
          console.error('Error in cross-sheet verdict subscription:', err),
      });
  }

  /**
   * Handles a `GET /conversion-listing` response for the current conversion —
   * the fileName/status refresh, the defensive phase set (see the caller's
   * comment for why it only applies when `conversionPhase()` is still null),
   * and setting `lastUsedEngine` from `resolveLastUsedEngine`, which drives
   * the engine picker's preselection on role0/role1.
   *
   * Extracted from the `ngOnInit` `fetchXbrlPreviewListing` subscribe purely
   * so `lastUsedEngine` end-to-end wiring is directly testable without
   * reconstructing the whole `ngOnInit` subscription graph — see
   * `filing-form-container-engine-payload.spec.ts`.
   */
  private applyConversionListingResponse(conversionData: ConversionData): void {
    this.fileName.set(conversionData.fileName || '');
    this.conversionStatus.set(conversionData.status ?? null);
    this.conversionAudit.set({
      conversionDate: conversionData.conversionDate,
      updatedDate: conversionData.updatedDate,
      createdBy: conversionData.createdBy,
      updatedBy: conversionData.updatedBy,
    });
    this.lastUsedEngine.set(resolveLastUsedEngine(conversionData));
    if (conversionData.phase !== undefined && this.conversionPhase() === null) {
      this.conversionPhase.set(conversionData.phase);
      this.stateService.setConversionPhase(conversionData.phase);
      this.enforcePhaseGatedSheet();
    }
    // Phase is now known (or the listing didn't carry one and we can fall
    // through to Phase 1 defaults) — wire up the phase-aware filing-info
    // subscriptions. Idempotent.
    this.setupActiveFilingInfoSubscriptions();
  }

  /**
   * Subscribe to the company-name field and the Role-0/Role-1 method fields
   * on whichever filing-info slot is authoritative for the current phase
   * (filingPrerequisites in Phase 1, filingInfo in Phase 2). Hydration
   * deliberately leaves the inactive slot empty (see the comment around the
   * isPhase2 branch in fetchAllMbrsData), so binding to the wrong slot would
   * silently freeze the title bar and getEnabledSheets() against an empty
   * record.
   *
   * Safe to call multiple times — guarded by activeFilingInfoSubscriptionsReady.
   */
  private setupActiveFilingInfoSubscriptions(): void {
    if (this.activeFilingInfoSubscriptionsReady) {
      return;
    }
    this.activeFilingInfoSubscriptionsReady = true;

    const activeKey = this.stateService.getActiveFilingInfoKey();

    // Seed Role-21 description from the active slot now that we know the phase.
    const accountingStandard =
      this.stateService.getState()[activeKey][
        'ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'
      ];
    this.updateRole21Description(accountingStandard);

    // Reactive company name updates from the active slot.
    this.companyNameSubscription = this.stateService.state$
      .pipe(map((state) => state[activeKey]['ssmt-dei:NameOfReportingEntity']))
      .subscribe((companyName) => {
        if (companyName) {
          this.companyName.set(companyName);
        }
      });

    // React to method-field / audit-status / accounting-standard edits on the
    // active slot and recompute filteredSheets + Role-21 description.
    this.methodFieldsSubscription = this.stateService.state$
      .pipe(
        map((state) => ({
          methodFinancialPosition:
            state[activeKey]['ssmt:MethodUsedForPreparingStatementOfFinancialPosition'],
          methodProfitLoss:
            state[activeKey]['ssmt:MethodUsedForPreparingStatementOfProfitOrLoss'],
          methodComprehensiveIncome:
            state[activeKey]['ssmt:MethodUsedForPreparingStatementOfComprehensiveIncome'],
          methodCashFlows:
            state[activeKey]['ssmt:MethodUsedForPreparingStatementOfCashFlows'],
          auditStatus:
            state[activeKey]['ssmt-dei:DisclosureOfFinancialStatementsAuditStatus'],
          accountingStandard:
            state[activeKey][
              'ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'
            ],
        })),
        distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current))
      )
      .subscribe((fields) => {
        this.filteredSheets = this.getEnabledSheets();
        this.updateRole21Description(fields.accountingStandard);
        this.validateCurrentSheet();
      });
  }

  ngOnDestroy(): void {
    // Close every in-flight load trace without reporting. Leaving the filing
    // form abandons the load rather than failing it, and a stall watchdog that
    // outlived this component would fire a "sheet didn't load" toast onto
    // whatever page the user navigated to.
    this.loadTrace.abandonAll();

    // Withdraw the mounted-sheet probe FIRST. Once this component is gone this
    // tab has no sheet on screen, so it must stop vouching for one — otherwise
    // a torn-down tab could keep a sibling's abandoned edits alive.
    this.stateService.clearMountedSheetProbe();

    // Clean up subscriptions to prevent memory leaks
    if (this.companyNameSubscription) {
      this.companyNameSubscription.unsubscribe();
    }
    if (this.methodFieldsSubscription) {
      this.methodFieldsSubscription.unsubscribe();
    }
    if (this.stateChangeSubscription) {
      this.stateChangeSubscription.unsubscribe();
    }
    if (this.validationSubscription) {
      this.validationSubscription.unsubscribe();
    }
    if (this.pendingValidationSubscription) {
      this.pendingValidationSubscription.unsubscribe();
    }
    if (this.crossSheetVerdictSubscription) {
      this.crossSheetVerdictSubscription.unsubscribe();
    }
  }

  // ngAfterViewInit intentionally does not reveal sheet validation errors.
  //
  // It used to, via `setTimeout(() => this.triggerValidationForCurrentSheet(), 300)`,
  // and that was the cause of "the mandatory-field errors sometimes don't show
  // after a refresh". That method reaches into the active sheet component for its
  // FormGroup and silently does nothing when the property is still `undefined` —
  // the normal state during a sheet's async `ngOnInit`, which only assigns the
  // form after its schema fetches resolve. Scope of Filing lost that race most
  // often (three fetches, one of them a ~640 KB MSIC options file), and losing it
  // meant NO field on the sheet ever showed an error, because the inline messages
  // are gated on `touched`. Nothing retried.
  //
  // Ownership now sits with each sheet, which knows when its own form is ready:
  // every form-bearing sheet calls `revealValidationErrors` from its init (roles
  // 0-4, 6 and 7 inherit it through `finalizeSheetInit`). That is deterministic,
  // and it also applies the computed-control carve-out that a blanket
  // `markAllAsTouched` from here could not.

  /**
   * Get required and optional sheets based on Type of Submission
   * KFI (Key Financial Items) = Minimal sheets
   * FS (Full Statements) = All applicable sheets
   */
  getRequiredSheets(): { required: SheetType[]; optional: SheetType[] } {
    // Phase-aware: read TypeOfSubmission from the active filing-info source
    // (filingPrerequisites in Phase 1, filingInfo in Phase 2). The inclusion
    // logic itself lives in SheetApplicabilityService (single source of truth).
    const typeOfSubmission =
      this.stateService.getActiveFilingInfoData()['ssmt-dei:TypeOfSubmission'] ||
      '';
    return this.sheetApplicability.computeRequiredSheets(typeOfSubmission);
  }

  /**
   * Check if a sheet is required based on Type of Submission
   */
  isSheetRequired(sheetValue: SheetType): boolean {
    const { required } = this.getRequiredSheets();
    return required.includes(sheetValue);
  }

  /**
   * Load scenarios configuration from JSON file
   */
  private async loadScenariosConfig(): Promise<void> {
    const config = await fetchJsonAsset<{ scenarios?: unknown }>(SCENARIOS_CONFIG_PATH);
    // Valid JSON of the wrong shape used to read as "no scenarios" and hide
    // nothing. It is a PARSE fault: the file arrived but is not what we expect.
    if (!config || !Array.isArray(config.scenarios)) {
      throw new SchemaAssetError(
        'PARSE',
        SCENARIOS_CONFIG_PATH,
        `${SCENARIOS_CONFIG_PATH} has no "scenarios" list`,
      );
    }
    this.scenariosConfig = config;
    // Note: getEnabledSheets() is not called here — the data fetch waits for this
    // load, and the methodFieldsSubscription recalculates filteredSheets as needed.
  }

  /** The scenarios load, or an already-settled promise for a host built without `ngOnInit`. */
  private awaitScenarios(): Promise<void> {
    return this.scenariosReady ?? Promise.resolve();
  }

  /**
   * Check if current filing data matches scenario conditions
   * Generic method that works with JSON-based scenario configuration
   */
  private matchesScenario(scenarioId: string): boolean {
    if (!this.scenariosConfig || !this.scenariosConfig.scenarios) {
      return false;
    }

    const scenario = this.scenariosConfig.scenarios.find(
      (scenario: any) => scenario.id === scenarioId
    );
    if (!scenario) {
      return false;
    }

    // Check conditions for each sheet
    const sheets = Object.keys(scenario.conditions);
    for (const sheetKey of sheets) {
      // The scenarios.config.json uses "filingInfo" as a logical label for
      // "the active filing-info source". Translate it per phase: Phase 1
      // reads from filingPrerequisites (role0 is the editable source);
      // Phase 2 reads from filingInfo (role1 is the editable source).
      // Other sheet keys pass through unchanged.
      const effectiveKey =
        sheetKey === SHEET_KEYS.filingInfo
          ? this.stateService.getActiveFilingInfoKey()
          : (sheetKey as keyof FilingFormState);

      const sheetData = this.stateService.getSheetData(effectiveKey);
      const conditions = scenario.conditions[sheetKey];

      // Check all field conditions for this sheet
      for (const [fieldName, expectedValue] of Object.entries(conditions)) {
        const actualValue = sheetData[fieldName];
        if (actualValue !== expectedValue) {
          return false; // Condition not met
        }
      }
    }

    return true;
  }

  // OLD HARDCODED METHODS REMOVED - NOW USING JSON-BASED CONFIGURATION
  // See: scenarios.config.json and matchesScenario() method

  /**
   * Get list of roles that should be hidden based on current scenario
   * Uses JSON-based scenario configuration for easy maintenance
   * Returns empty array if no scenario matches
   */
  private getScenarioHiddenRoles(): SheetType[] {
    if (!this.scenariosConfig || !this.scenariosConfig.scenarios) {
      return [];
    }

    // Check all scenarios and collect hidden roles from matching scenarios
    const hiddenRoles: SheetType[] = [];

    for (const scenario of this.scenariosConfig.scenarios) {
      if (this.matchesScenario(scenario.id)) {
        // Scenario matched - add its hidden roles
        hiddenRoles.push(...scenario.hideRoles);
      }
    }

    return hiddenRoles;
  }

  /**
   * Check if the active filing-info source is completed.
   *
   * Feeds `EnabledSheetsInput.isActiveFilingInfoValid`, which gates downstream
   * sheet access in Phase 1 and unresolved-phase only — Phase 2 waives it. The
   * predicate itself stays phase-agnostic on purpose: the waiver belongs in
   * SheetApplicabilityService, next to the other clamps. The "active
   * filing-info source"
   * is role0 (filingPrerequisites) during Phase 1 and role1 (filingInfo)
   * during Phase 2 — see FilingFormStateService.getActiveFilingInfoKey().
   *
   * Renamed from isRole0Completed() in PR-D: the semantic is "the editable
   * filing-info sheet for the current phase is complete", not "role0
   * specifically is complete". In Phase 2 role0 is hidden and frozen; what
   * matters to the downstream gate is whether role1 is filled in.
   */
  isActiveFilingInfoCompleted(): boolean {
    const activeKey = this.stateService.getActiveFilingInfoKey();
    const sheetLevelStatus = this.stateService.getSheetLevelStatus(activeKey);
    const data = this.stateService.getSheetData(activeKey);
    const hasData = data && Object.keys(data).length > 0;

    return !sheetLevelStatus.hasErrors && hasData;
  }

  /**
   * Check if MbrsData from API exists
   * Returns true if data exists and has at least one sheet with meaningful data
   * Meaningful data means: not null, not undefined, not empty object
   *
   * NAVIGATE-BACK FALLBACK: The primary source is the component-local
   * allSheetsData signal, which is populated by fetchAllMbrsData() on first
   * load. That signal is reset to {} on every component mount, so on SPA
   * navigate-back (where replayPostLoadUiWiring skips the re-fetch to preserve
   * unsubmitted edits) we fall back to the singleton FilingFormStateService,
   * which persists the backend data in memory across component tear-downs
   * within the same JS context. Without this fallback, getEnabledSheets()
   * would hard-block to only filing-prerequisites on navigate-back, causing
   * validateCurrentSheet() to reset the user's saved sheet to Role 0.
   */
  hasMbrsData(): boolean {
    const mbrsData = this.allSheetsData();

    // Helper to check if a key is FilingInfo (case-insensitive, various formats)
    const isFilingInfoKey = (key: string): boolean => {
      const normalizedKey = key.toLowerCase().replace(/[-_]/g, '');
      return normalizedKey === 'filinginfo';
    };

    // Primary: check component-local signal (populated by fetchAllMbrsData)
    if (mbrsData && typeof mbrsData === 'object' && !Array.isArray(mbrsData)) {
      const keys = Object.keys(mbrsData).filter(key => !isFilingInfoKey(key));
      if (keys.length > 0) {
        const hasData = keys.some(key => {
          const sheetData = (mbrsData as any)[key];
          if (!sheetData) return false; // null or undefined
          if (typeof sheetData !== 'object') return true; // primitive value
          if (Array.isArray(sheetData)) return sheetData.length > 0;
          return Object.keys(sheetData).length > 0; // non-empty object
        });
        if (hasData) return true;
      }
    } else if (Array.isArray(mbrsData)) {
      if (mbrsData.length > 0) return true;
    }

    // Fallback: check the singleton state service. On navigate-back within the
    // same JS context, the component-local allSheetsData signal is reset to {}
    // but FilingFormStateService.originalStateSubject still holds the loaded
    // backend data. Any non-empty, non-FilingInfo sheet counts as "has data".
    const originalState = this.stateService.getOriginalState();
    if (!originalState) return false;

    for (const [sheetKey, sheetData] of Object.entries(originalState)) {
      if (isFilingInfoKey(sheetKey)) continue;
      if (sheetData && typeof sheetData === 'object' && Object.keys(sheetData).length > 0) {
        return true;
      }
    }

    return false;
  }

  /**
   * Recompute the sheet list and snap `currentSheet` to a phase-legal value
   * after the backend-supplied phase arrives. Without this, a user landing on
   * the filing page via a saved-sheet restore (localStorage) could still see
   * a sheet selected that the current phase's filter now hides.
   *
   *  - Phase 1 → snap to 'filing-prerequisites' (role0 is the only legal
   *    selection; all other sheets are hidden by the Phase 1 gate).
   *  - Phase 2 → snap off 'filing-prerequisites' specifically when the user
   *    was restored onto role0, since PR-E hides role0 in Phase 2. 'filing-info'
   *    (role1) is the legal replacement. Other Phase 2 selections stay put.
   *  - Unknown phase → no snap; wait for the backend to report.
   */
  /**
   * The sheet explicitly requested via the `?sheet=` URL query param, or null
   * when absent/invalid.
   *
   * Entries that arrive WITHOUT a sheet param are the "fresh entry from the
   * xbrl-files listing" case (the listing navigates with only id / companyName /
   * fileName) — those land on the Phase-2 default (`filing-info`). Entries WITH
   * an explicit, valid sheet param are a "specific page" request — a field-link
   * deep-link (opened in a new tab, also carrying `?scrollTo=`) or a refresh of
   * a sheet the user was on — and must be honoured instead of snapping to
   * `filing-info`.
   *
   * On a cold load the target isn't enabled yet (during hydration
   * `getEnabledSheets()` is gated to `filing-prerequisites`), so this only
   * classifies the request; enabled-ness is checked at apply time after
   * hydration completes.
   */
  private getRequestedSheetFromUrl(): SheetType | null {
    const sheet = this.route.snapshot.queryParamMap.get('sheet');
    return sheet && this.isValidSheet(sheet) ? (sheet as SheetType) : null;
  }

  private enforcePhaseGatedSheet(): void {
    this.filteredSheets = this.getEnabledSheets();

    const phase = this.conversionPhase();

    if (phase === ConversionPhaseEnum.Phase1) {
      if (this.currentSheet !== 'filing-prerequisites') {
        this.currentSheet = 'filing-prerequisites';
        this.persistence.putCurrentSheet(this.conversionId() ?? '', this.currentSheet);
      }
      this.isInitialPhaseResolution = false;
      // The landing sheet is now settled. Set in the same synchronous block as
      // the `currentSheet` write above so no change-detection tick can observe
      // one without the other — see `landingSheetResolved`.
      this.landingSheetResolved.set(true);
      return;
    }

    if (phase === ConversionPhaseEnum.Phase2) {
      // Explicit deep-link / refresh override: when the URL names a specific
      // sheet (a field-link opened in a new tab, or a refresh of the sheet the
      // user was on), honour it instead of snapping to 'filing-info'. Only
      // entries WITHOUT a sheet param (the xbrl-files listing → filing flow)
      // fall through to the default below. The actual landing on the requested
      // sheet happens in the post-hydration restore once it is enabled — here we
      // only avoid clobbering it. Reading the URL (not a captured flag) keeps
      // this race-free against fetchAllMbrsData completing before/after the
      // conversion-listing fetch that calls this method.
      if (this.getRequestedSheetFromUrl()) {
        this.isInitialPhaseResolution = false;
        // `landingSheetResolved` is deliberately NOT set here: this branch does
        // not touch `currentSheet`, it defers to the post-hydration restore. A
        // refresh of the Filing Information tab itself arrives as
        // `?sheet=filing-info` while `currentSheet` is still the default, which
        // is the same cold-mount window the flag exists to close. The restore
        // sets it once it has applied the requested sheet.
        return;
      }

      // On first phase resolution for this component instance (i.e. on every
      // entry to /mbrs/filing — refresh, tab reopen, or in-app navigation
      // from another route), Phase 2 always lands on 'filing-info'. This
      // pairs with the refresh-discard policy in FilingFormStateService:
      // unsaved edits are gone, so dropping the user back where they were
      // would be misleading. In-session sheet switches go through
      // onSheetChange/prev/next and bypass this method.
      if (this.isInitialPhaseResolution) {
        if (this.currentSheet !== 'filing-info') {
          this.currentSheet = 'filing-info';
          this.persistence.putCurrentSheet(this.conversionId() ?? '', this.currentSheet);
        }
        this.isInitialPhaseResolution = false;
      } else if (this.currentSheet === 'filing-prerequisites') {
        // Defensive fallback: role0 is hidden in Phase 2, so if anything
        // restored the user onto it after the initial resolution, snap to
        // the legal Phase 2 gate sheet.
        this.currentSheet = 'filing-info';
        this.persistence.putCurrentSheet(this.conversionId() ?? '', this.currentSheet);
      }
      // Settled by one of the two branches above (or already legal). Same
      // synchronous block as the `currentSheet` writes — see the Phase-1 arm.
      this.landingSheetResolved.set(true);
      return;
    }

    // Phase still unresolved (`null`): `currentSheet` is untrustworthy, so
    // `landingSheetResolved` stays false and the cross-tab block stays shut.
  }

  /**
   * Get enabled sheets based on Role-1 method configuration AND submission type
   * Combines method-based filtering with KFI/FS requirements AND scenario-based hiding
   *
   * HARD BLOCK (owned by SheetApplicabilityService.computeEnabledSheets):
   *  - DATA AVAILABILITY — MbrsData missing or mid-hydration clamps to the
   *    phase's gate sheet alone, in EVERY phase.
   *  - VALIDITY — an incomplete gate sheet clamps in Phase 1 and unresolved
   *    (`null`) phase only. **Phase 2 does not clamp on validity**, because
   *    role1 republishes `hasErrors` on every debounced keystroke and the
   *    clamp made the sheet selector collapse to one entry mid-edit. See the
   *    VALIDITY clamp comment in the service.
   */
  getEnabledSheets(): SheetOption[] {
    // Applicability logic lives in SheetApplicabilityService (single source of
    // truth, shared with getRequiredSheets). This method's only job is to
    // resolve the phase-aware STATE reads and hand the resulting scalars to the
    // pure service.
    //
    // Phase-aware reads (PR-D Option X):
    //  - filing-info fields (TypeOfSubmission, AuditStatus) live on the active
    //    filing-info source: filingPrerequisites in Phase 1 (role0 superset) OR
    //    filingInfo in Phase 2 (role1 27-field subset).
    //  - scope-of-filing fields (method fields) live on filingPrerequisites in
    //    Phase 1 OR scopeOfFiling in Phase 2 (role2 is authoritative).
    const phase = this.conversionPhase();
    const filingData = this.stateService.getActiveFilingInfoData();
    const scopeData =
      phase === ConversionPhaseEnum.Phase2
        ? this.stateService.getSheetData(SHEET_KEYS.scopeOfFiling)
        : filingData;

    return this.sheetApplicability.computeEnabledSheets({
      sheets: this.sheets,
      phase,
      isActiveFilingInfoValid: this.isActiveFilingInfoCompleted(),
      hasMbrsData: this.hasMbrsData(),
      isHydratingMbrsData: this.stateService.isHydratingMbrsData(),
      typeOfSubmission: filingData['ssmt-dei:TypeOfSubmission'] || '',
      auditStatus:
        filingData['ssmt-dei:DisclosureOfFinancialStatementsAuditStatus'] || '',
      methodFinancialPosition:
        scopeData['ssmt:MethodUsedForPreparingStatementOfFinancialPosition'] ||
        '',
      methodProfitLoss:
        scopeData['ssmt:MethodUsedForPreparingStatementOfProfitOrLoss'] || '',
      methodComprehensiveIncome:
        scopeData[
          'ssmt:MethodUsedForPreparingStatementOfComprehensiveIncome'
        ] || '',
      methodCashFlows:
        scopeData['ssmt:MethodUsedForPreparingStatementOfCashFlows'] || '',
      scenarioHiddenRoles: this.getScenarioHiddenRoles(),
    });
  }

  /**
   * Check if sheet value exists in the overall sheets list
   */
  private isValidSheet(value: string): boolean {
    return this.sheets.some((sheet) => sheet.value === value);
  }

  /**
   * Check if sheet is currently enabled based on Role-1 configuration
   */
  private isSheetEnabled(value: SheetType): boolean {
    return this.filteredSheets.some((sheet) => sheet.value === value);
  }

  /**
   * Validate and fix current sheet if it's disabled
   * Redirects to first enabled sheet if current sheet is not in filtered list
   */
  private validateCurrentSheet(): void {
    // Skip validation while MBRS data is loading — filteredSheets only contains
    // filing-prerequisites during this phase, so any restored sheet would be
    // incorrectly flagged as unavailable. The authoritative validation runs
    // after fetchAllMbrsData completes and filteredSheets are fully computed.
    if (this.stateService.isHydratingMbrsData()) {
      return;
    }

    // Skip during background validation cycle — the cycle temporarily renders
    // each sheet component in a hidden container for validation. This can cause
    // transient sheetLevelStatus changes (e.g., filing-prerequisites reporting
    // hasErrors: true) that make getEnabledSheets() return only ['filing-prerequisites'].
    // If a state$ emission triggers validateCurrentSheet at that moment, it would
    // incorrectly overwrite the user's saved sheet in localStorage.
    if (this.isRunningValidationCycle) {
      return;
    }

    if (!this.isSheetEnabled(this.currentSheet)) {
      // Redirect to first enabled sheet (default to filing-info) for the
      // current render cycle only. We deliberately do NOT persist this
      // redirect to localStorage — validateCurrentSheet() can fire with a
      // transient filteredSheets list during component init or status
      // re-emits (e.g., navigate-back before allSheetsData is repopulated),
      // and persisting the transient fallback would permanently lose the
      // user's saved sheet selection. Persistence is reserved for explicit
      // user actions: onSheetChange, previousSheet, nextSheet. If the sheet
      // becomes genuinely unavailable due to a Role-1 configuration change,
      // the next explicit navigation will overwrite the stale localStorage
      // entry at that time.
      const firstEnabledSheet = this.filteredSheets[0]?.value || 'filing-info';
      const previousSheet = this.currentSheet;
      this.currentSheet = firstEnabledSheet;

      // Phase-transition snap: in Phase 2 role0 is hidden, so a restored
      // or defaulted 'filing-prerequisites' selection is expected to flip
      // to 'filing-info'. Same story for Phase 1 if somehow a Phase-2 sheet
      // is selected. These are not configuration mismatches — no toast.
      const isPhaseTransitionSnap =
        (this.conversionPhase() === ConversionPhaseEnum.Phase2 &&
          previousSheet === 'filing-prerequisites') ||
        (this.conversionPhase() === ConversionPhaseEnum.Phase1 &&
          previousSheet === 'filing-info');

      if (isPhaseTransitionSnap) {
        return;
      }

      this.messageService.add({
        severity: 'warn',
        summary: 'Sheet Not Available',
        detail:
          'The selected sheet is not available based on your filing configuration. Redirected to first available sheet.',
        life: TOAST_DURATION_LONG_MS,
      });
    }
  }

  /**
   * Sheet dropdown handler. Re-checks the session FIRST (P.O., 2026-10-08):
   * on an ended session `SessionGateService` has already sent the user to
   * login, so neither the new sheet nor the gate-sheet unsaved-changes dialog
   * in `applySheetChange` is reached.
   */
  async onSheetChange(newSheet: SheetType): Promise<void> {
    if (!(await this.sessionGate.confirmSessionBeforeLeaving(this.currentSheetDiagnosticScope())))
      return;
    this.applySheetChange(newSheet);
  }

  /**
   * Applies a `?sheet=` change from the URL: field links
   * (`field-link-label`, `field-link-back-chooser`) and the initial load.
   * Later changes re-check the session first (P.O., 2026-10-08); the first
   * emission does not, because the route guard has just checked it. The
   * container's own URL sync after a dropdown switch arrives with
   * `sheetParam === currentSheet` and returns before the check.
   */
  private async applySheetFromQueryParam(params: Params): Promise<void> {
    const isFirstEmission = !this.hasAppliedSheetParam;
    this.hasAppliedSheetParam = true;

    const sheetParam = params['sheet'];
    if (
      !sheetParam ||
      !this.isValidSheet(sheetParam) ||
      sheetParam === this.currentSheet
    ) {
      return;
    }

    if (!isFirstEmission && !(await this.sessionGate.confirmSessionBeforeLeaving(this.currentSheetDiagnosticScope())))
      return;

    // Validate that the target sheet is enabled
    if (this.isSheetEnabled(sheetParam as SheetType)) {
      this.currentSheet = sheetParam as SheetType;
      this.persistence.putCurrentSheet(this.conversionId() ?? '', sheetParam);
    } else {
      // Sheet is not enabled based on current configuration.
      //
      // Drop any field-link target riding along with it. Since
      // FieldLinkTargetDirective only consumes an arrival ADDRESSED to its
      // own sheet, nobody else will ever clear this one — the addressee is
      // never going to mount. Left on the URL it would survive into a later
      // navigation via queryParamsHandling: 'merge' and fire a stale scroll
      // whenever the user eventually enables and opens that sheet.
      if (params[FIELD_LINK_SCROLL_PARAM]) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { [FIELD_LINK_SCROLL_PARAM]: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }
    }
  }

  /**
   * Handle sheet change
   * CRITICAL: Blocks navigation when leaving a filing-info gate sheet
   * (role0 'filing-prerequisites' OR role1 'filing-info') with unsaved
   * changes — both gate sheets drive downstream sheet enablement and
   * any unsaved edits would be silently lost on navigation.
   */
  private applySheetChange(newSheet: SheetType): void {
    // Validate that the new sheet is enabled
    if (!this.isSheetEnabled(newSheet)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Sheet Not Available',
        detail:
          'This sheet is not available based on your current filing configuration in Role-1.',
        life: TOAST_DURATION_LONG_MS,
      });
      return;
    }

    // CRITICAL: Block navigation if leaving a filing-info gate sheet with
    // unsaved changes (only when conversionId exists — first-time mode is
    // exempt because there is nothing to save against yet).
    const gate = FILING_INFO_GATES.find(
      (group) => group.sheet === this.currentSheet
    );
    if (
      gate &&
      newSheet !== gate.sheet &&
      this.conversionId()
    ) {
      const hasModifications = this.stateService.isSheetModified(gate.stateKey);

      if (hasModifications) {
        // Store the target sheet for later navigation if user chooses to discard
        this.pendingNavigationTarget = newSheet;

        // Form has unsaved changes - show blocking dialog
        this.isNavigationBlockDialogOpen.set(true);

        // Force the dropdown to revert using ViewChild (doesn't destroy components)
        // Use setTimeout to ensure PrimeNG has finished its internal update
        setTimeout(() => {
          if (this.sheetSelector) {
            // Revert dropdown back to the current gate sheet (not the target)
            this.sheetSelector.writeValue(gate.sheet);
          }
        }, 0);

        return;
      }
    }

    this.currentSheet = newSheet;

    // Unlock the sheet status so the active component can report validation updates.
    // The lock was set by the background validation cycle to block late setTimeout writes.
    const newStateKey = this.sheetTypeToStateKey[newSheet] as string;
    if (newStateKey) {
      this.stateService.unlockSheetStatus(newStateKey);
    }

    // Save current sheet selection (scoped to conversionId)
    this.persistence.putCurrentSheet(this.conversionId() ?? '', newSheet);

    // Update URL query parameters to trigger navigation detection for linkTo
    const queryParams = {
      ...this.route.snapshot.queryParams,
      sheet: newSheet,
    };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    // No error-reveal call here: the newly-mounted sheet reveals its own errors
    // from its own init via `revealValidationErrors`, which is deterministic
    // where this container-side timer was not. See the note on `ngAfterViewInit`.
  }

  /**
   * Toggle navigation block dialog visibility
   */
  toggleNavigationBlockDialog(isOpen: boolean): void {
    this.isNavigationBlockDialogOpen.set(isOpen);
  }

  /**
   * Handle Close button click in navigation block dialog
   */
  onNavigationBlockClose(): void {
    this.isNavigationBlockDialogOpen.set(false);
    // this.pendingNavigationTarget = null; // Clear pending navigation
  }

  /**
   * Handle Discard and Continue button click in navigation block dialog
   * Discards Role-0 modifications and proceeds to the target sheet
   */
  // onNavigationBlockDiscard(): void {
  //   // Close the dialog
  //   this.isNavigationBlockDialogOpen.set(false);

  //   // Reset Role-0 data to original state (discard modifications)
  //   const originalData = this.stateService.getOriginalSheetData('filingPrerequisites');
  //   this.stateService.updateSheetData('filingPrerequisites', originalData);

  //   // Navigate to the pending target sheet
  //   if (this.pendingNavigationTarget) {
  //     const targetSheet = this.pendingNavigationTarget;
  //     this.pendingNavigationTarget = null; // Clear pending navigation

  //     // Navigate to target sheet
  //     this.currentSheet = targetSheet;
  //     localStorage.setItem(this.getCurrentSheetStorageKey(), targetSheet);

  //     // Update URL
  //     const queryParams = {
  //       ...this.route.snapshot.queryParams,
  //       sheet: targetSheet,
  //     };
  //     this.router.navigate([], {
  //       relativeTo: this.route,
  //       queryParams: queryParams,
  //       queryParamsHandling: 'merge',
  //       replaceUrl: true,
  //     });

  //     // Trigger validation for the new sheet
  //     setTimeout(() => {
  //       this.triggerValidationForCurrentSheet();
  //     }, 300); // Increased timeout to ensure component is fully rendered

  //     // Show confirmation message
  //     this.messageService.add({
  //       severity: 'info',
  //       summary: 'Changes Discarded',
  //       detail: 'Filing Prerequisites changes have been discarded',
  //       life: 3000,
  //     });
  //   }
  // }

  /**
   * Get current sheet index (1-based) - uses filteredSheets
   */
  getCurrentSheetIndex(): number {
    return (
      this.filteredSheets.findIndex((sheet) => sheet.value === this.currentSheet) + 1
    );
  }

  /**
   * The `S<role>` scope a conversion-wide load failure prints — the sheet the
   * user was trying to reach, e.g. `S8` for Statement of Financial Position.
   * Handed to `SheetLoadTraceService.beginContainerLoad` as a FUNCTION and
   * called only when the load fails; see that method for why.
   *
   * `?sheet=` wins over `currentSheet`. A failed load never enables the
   * requested sheet — enabled-ness needs the very data that failed to arrive —
   * so `applySheetFromQueryParam` declines it and `currentSheet` stays on the
   * saved or default sheet. The URL still says what the user asked for.
   *
   * Uses the role label (`SHEET_DIAGNOSTIC_ROLE_LABELS`), never the sheet's
   * position in `sheets` — that order follows the Prep Tool, not the role
   * numbers, so a position disagreed with every other code for the same sheet.
   */
  private resolveLoadFailureSheetScope(): string {
    const requestedSheet = this.getRequestedSheetFromUrl();
    // Until the landing sheet is settled, `currentSheet` is only the
    // construction-time default ('filing-prerequisites'), which is wrong for a
    // Phase-2 conversion (it lands on Filing Information). Naming S0 there sent
    // a reporter to the wrong sheet, so never guess from `currentSheet`. The
    // listing row's phase hint (router state, see `filingEntryState`) is not a
    // guess — it names the sheet the entry lands on; without it print bare `S`.
    if (requestedSheet == null && !this.landingSheetResolved()) {
      return scopeFromFilingEntryState(window.history.state) ?? filingSheetScope(null);
    }
    return filingSheetScope(requestedSheet ?? this.currentSheet);
  }

  /**
   * The scope and conversion an expired session on THIS page's load is
   * reported under — the same pair `beginContainerLoad` prints, so every
   * AUTH code raised while the filing page loads reads
   * `MBRS-S<role>-AUTH-<conversion>`, whichever request hit the 401.
   */
  private loadFailureDiagnosticScope(): DiagnosticScope {
    return { scope: this.resolveLoadFailureSheetScope(), conversionId: this.conversionId() };
  }

  /** The login-page line for an expired session on this page's load. */
  private loadFailureAuthSummary(): string {
    const { scope, conversionId } = this.loadFailureDiagnosticScope();
    return `${LOAD_FAILURE_COPY.AUTH} ${formatToastDiagnosticCode(scope, 'AUTH', conversionId)}`;
  }

  /**
   * Get total number of enabled sheets
   */
  getTotalSheets(): number {
    return this.filteredSheets.length;
  }

  /**
   * Get current sheet label
   */
  getCurrentSheetLabel(): string {
    const sheet = this.sheets.find((sheet) => sheet.value === this.currentSheet);
    return sheet ? sheet.label : '';
  }

  /**
   * Get current sheet description
   */
  getCurrentSheetDescription(): string {
    const sheet = this.sheets.find((sheet) => sheet.value === this.currentSheet);
    return sheet ? sheet.description : '';
  }

  /** Re-checks the session first — see `onSheetChange`. Not bound in any template today; gated so a future binding cannot bypass the check. */
  async previousSheet(): Promise<void> {
    if (!(await this.sessionGate.confirmSessionBeforeLeaving(this.currentSheetDiagnosticScope())))
      return;
    this.applyPreviousSheet();
  }

  /**
   * Navigate to previous sheet (only through enabled sheets)
   *
   * Developer Note (status-lock asymmetry):
   *   Unlike applySheetChange() — which calls
   *   `this.stateService.unlockSheetStatus(newStateKey)` — this method does
   *   NOT unlock the newly-current sheet's status. The background validation
   *   cycle in runBackgroundValidationCycle() locks every sheet after it
   *   reports its status, so late setTimeout callbacks from destroyed
   *   components cannot overwrite finalized statuses. When the user navigates
   *   here via previous/next buttons (instead of the dropdown), the lock
   *   stays in place — which is fine for reading the cached status but
   *   blocks the newly-mounted component from writing live updates via
   *   updateSheetLevelStatus() until something else unlocks it.
   *
   *   This has NOT been observed as a visible regression, but if a user
   *   reports stale spinners or "validation never updates" on prev/next
   *   navigation, mirror the applySheetChange() unlock logic here and in
   *   applyNextSheet(). Kept un-fixed for now to avoid scope creep in the
   *   refresh-discard / navigate-back hotfix.
   */
  private applyPreviousSheet(): void {
    const currentIndex = this.filteredSheets.findIndex(
      (sheet) => sheet.value === this.currentSheet
    );
    if (currentIndex > 0) {
      this.currentSheet = this.filteredSheets[currentIndex - 1].value;
      this.persistence.putCurrentSheet(this.conversionId() ?? '', this.currentSheet);

      // Update URL query parameters
      const queryParams = {
        ...this.route.snapshot.queryParams,
        sheet: this.currentSheet,
      };
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: queryParams,
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  /** Re-checks the session first — see `onSheetChange`. Not bound in any template today; gated so a future binding cannot bypass the check. */
  async nextSheet(): Promise<void> {
    if (!(await this.sessionGate.confirmSessionBeforeLeaving(this.currentSheetDiagnosticScope())))
      return;
    this.applyNextSheet();
  }

  /**
   * Navigate to next sheet (only through enabled sheets)
   *
   * Developer Note (status-lock asymmetry):
   *   See the matching note on applyPreviousSheet() above. This method has the
   *   same un-fixed asymmetry with applySheetChange(): the newly-current
   *   sheet's status is NOT unlocked here, only via the dropdown navigation
   *   path. If prev/next buttons start producing stale-spinner reports,
   *   call `this.stateService.unlockSheetStatus(newStateKey)` after
   *   assigning `this.currentSheet` in both methods.
   */
  private applyNextSheet(): void {
    const currentIndex = this.filteredSheets.findIndex(
      (sheet) => sheet.value === this.currentSheet
    );
    if (currentIndex < this.filteredSheets.length - 1) {
      this.currentSheet = this.filteredSheets[currentIndex + 1].value;
      this.persistence.putCurrentSheet(this.conversionId() ?? '', this.currentSheet);

      // Update URL query parameters
      const queryParams = {
        ...this.route.snapshot.queryParams,
        sheet: this.currentSheet,
      };
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: queryParams,
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  /**
   * Check if can go to previous sheet
   */
  canGoPrevious(): boolean {
    return this.getCurrentSheetIndex() > 1;
  }

  /**
   * Check if can go to next sheet
   */
  canGoNext(): boolean {
    return this.getCurrentSheetIndex() < this.getTotalSheets();
  }

  /**
   * Get completion status for all sheets
   */
  getCompletionStatus(): { [key: string]: boolean } {
    return this.stateService.getCompletionStatus();
  }

  /**
   * Check if sheet has data
   */
  hasSheetData(sheetValue: SheetType): boolean {
    const stateKey = this.sheetTypeToStateKey[sheetValue];
    const status = this.stateService.getCompletionStatus();
    return (status as any)[stateKey] || false;
  }

  /**
   * Get sheet validation status
   * @returns 'valid' | 'invalid' | 'edited' | 'empty' (empty = pending validation, hide icon)
   */
  getSheetStatus(
    sheetValue: SheetType
  ): 'valid' | 'invalid' | 'edited' | 'empty' {
    const stateKey = this.sheetTypeToStateKey[sheetValue];

    // Reference stateVersion() to trigger re-renders when state changes
    void this.stateVersion();

    // 0. Check if frontend batch validation is still pending — hide icon
    if (this.stateService.isSheetValidationPending(stateKey as string)) {
      return 'empty';
    }

    // Priority: Edited > Failed > Validated
    // Show pen icon when actively editing (even if errors exist)
    // Only show red cross for forms with errors that haven't been edited yet

    // 1. Check if sheet has been edited (user has made changes via auto-save)
    const isEdited = this.stateService.getSheetEdited(stateKey);
    if (isEdited) {
      return 'edited';  // Show pen icon when user is actively working on the form
    }

    // 2. Check sheet-level status (hasErrors determines Failed vs Validated)
    // If no errors, show green checkmark regardless of whether sheet has data
    const sheetLevelStatus = this.stateService.getSheetLevelStatus(stateKey);
    if (sheetLevelStatus.hasErrors) {
      return 'invalid';  // Failed - show red cross for unedited forms with errors
    }

    // 3. Default: Validated (green checkmark) - no errors means validated
    return 'valid';
  }

  /**
   * Check if sheet has suggested changes (for red asterisk display)
   * Only returns true when status is Validated AND has suggested changes
   */
  hasSuggestedChanges(sheetValue: SheetType): boolean {
    const stateKey = this.sheetTypeToStateKey[sheetValue];

    // Reference stateVersion() to trigger re-renders when state changes
    void this.stateVersion();

    // Check if sheet has suggested changes (show asterisk regardless of status)
    const sheetLevelStatus = this.stateService.getSheetLevelStatus(stateKey);
    return sheetLevelStatus.hasSuggestedChanges;
  }

  /**
   * Get status icon class
   */
  getStatusIcon(status: 'valid' | 'invalid' | 'edited' | 'empty'): string {
    switch (status) {
      case 'valid':
        return 'pi pi-check-circle';
      case 'invalid':
        return 'pi pi-times-circle';
      case 'edited':
        return 'pi pi-pencil';
      case 'empty':
        return '';
    }
  }

  /**
   * Get status icon color
   */
  getStatusColor(status: 'valid' | 'invalid' | 'edited' | 'empty'): string {
    switch (status) {
      case 'valid':
        return '#22c55e';
      case 'invalid':
        return '#ef4444';
      case 'edited':
        return '#f59e0b';
      case 'empty':
        return '#94a3b8';
    }
  }

  /**
   * Get status text
   */
  getStatusText(status: 'valid' | 'invalid' | 'edited' | 'empty'): string {
    switch (status) {
      case 'valid':
        return 'Valid';
      case 'invalid':
        return 'Failed';
      case 'edited':
        return 'Edited';
      case 'empty':
        return '';
    }
  }

  /**
   * Refresh the pre-computed sheet status cache.
   * Called once per stateVersion change instead of per-sheet per-CD-cycle.
   */
  refreshSheetStatusCache(): void {
    for (const sheet of this.filteredSheets) {
      this.sheetStatusCache.set(sheet.value, {
        status: this.getSheetStatus(sheet.value),
        hasSuggested: this.hasSuggestedChanges(sheet.value),
      });
    }
  }

  /**
   * Get cached sheet status (for template use — avoids recomputation)
   */
  getCachedStatus(sheetValue: SheetType): 'valid' | 'invalid' | 'edited' | 'empty' {
    return this.sheetStatusCache.get(sheetValue)?.status ?? 'empty';
  }

  /**
   * Get cached hasSuggestedChanges (for template use — avoids recomputation)
   */
  getCachedHasSuggested(sheetValue: SheetType): boolean {
    return this.sheetStatusCache.get(sheetValue)?.hasSuggested ?? false;
  }

  /**
   * Can THIS sheet ever raise a suggested change?
   *
   * Drives the "(*) Optional: Suggestions to refine data" legend, which is a
   * caption for what the red `*` means — so it tracks capability, never the
   * live flag. Gating on the live flag would blink the caption in and out as
   * the user edits. `getCachedHasSuggested` answers the different question
   * ("does it have one right now?") and drives the selector's own `*`.
   */
  isSuggestionCapable(sheetValue: SheetType): boolean {
    return SUGGESTION_CAPABLE_SHEETS.has(this.sheetTypeToStateKey[sheetValue]);
  }

  /**
   * Clear all form data
   */
  clearAllData(): void {
    this.confirmationService.confirm({
      message:
        'Are you sure you want to clear all form data? This action cannot be undone.',
      header: 'Clear All Data',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.stateService.clearAllData();
        this.messageService.add({
          severity: 'success',
          summary: 'Data Cleared',
          detail: 'All form data has been cleared successfully',
          life: TOAST_DURATION_DEFAULT_MS,
        });
      },
    });
  }

  /**
   * Export all data as JSON
   */
  exportData(): void {
    const allData = this.stateService.exportAllData();
    const dataStr = JSON.stringify(allData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `mbrs-filing-data-${
      new Date().toISOString().split('T')[0]
    }.json`;
    link.click();
    window.URL.revokeObjectURL(url);

    this.messageService.add({
      severity: 'success',
      summary: 'Data Exported',
      detail: 'Form data has been exported successfully',
      life: TOAST_DURATION_DEFAULT_MS,
    });
  }

  /**
   * Import data from JSON file
   */
  importData(event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (loadEvent: any) => {
      try {
        const data = JSON.parse(loadEvent.target.result);
        this.stateService.importData(data);
        // Imported data invalidates all cached validation statuses
        this.stateService.clearValidationCache();

        this.messageService.add({
          severity: 'success',
          summary: 'Data Imported',
          detail: 'Form data has been imported successfully',
          life: TOAST_DURATION_DEFAULT_MS,
        });
      } catch (error) {
        this.messageService.add({
          severity: 'error',
          summary: 'Import Failed',
          detail: 'Invalid JSON file format',
          life: TOAST_DURATION_LONG_MS,
        });
      }
    };
    reader.readAsText(file);
  }

  /**
   * Get state key for a sheet value
   */
  private getStateKeyForSheet(sheetValue: SheetType): string {
    return this.sheetTypeToStateKey[sheetValue];
  }

  /**
   * Get progress percentage
   */
  getProgressPercentage(): number {
    const status = this.getCompletionStatus();
    const completed = Object.values(status).filter((isComplete) => isComplete).length;
    const total = this.getTotalSheets();
    return Math.round((completed / total) * 100);
  }

  /**
   * Trigger file input for import
   */
  triggerImport(): void {
    const fileInput = document.getElementById(
      'importFileInput'
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  goBack() {
    this.router.navigate(['/mbrs/xbrl-files']);
  }

  /**
   * Native browser prompt for tab-close / back / URL-change when unsaved edits
   * exist. Also fires on refresh — `beforeunload` cannot distinguish the two.
   *
   * This handler ONLY raises the prompt. It deliberately does **not** arm the
   * refresh-discard intent: `beforeunload` fires while the page is still alive
   * and BEFORE the user answers, so at this point "is the page leaving?" is
   * genuinely unknown. Arming happens in `onPageHide` instead.
   */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    // A failed load shows no sheet, so nothing on screen can be unsaved. Any
    // modified state is an earlier session's cache, kept across refresh/close
    // (and across Retry — see the Retry-hop bypass in `canDeactivateFilingGuard`).
    // In-app navigation away is NOT exempt; the guard still prompts there.
    if (this.loadFailure() !== null) return;
    if (this.stateService.hasAnySheetModified()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  /**
   * Arms the refresh-discard intent — the marker that lets the reloaded page
   * treat a confirmed refresh as "Continue without saving" and snap this tab's
   * modified state back to original. Scoped to THIS tab, so a sibling merely
   * opened on the same conversion is untouched; see
   * `FilingFormStateService.armUnloadDiscardIntent` and
   * `consumeRefreshDiscardFlag` for the full mechanism.
   *
   * **Why `pagehide` and not `beforeunload`.** Arming used to live in
   * `onBeforeUnload`, which fires before the user has answered "Reload site?",
   * so it had to compensate with a queued `setTimeout(…, 0)` that un-armed
   * itself on the premise "a real unload kills the timer". Instrumented browser
   * runs disproved that: the timer fired identically on Reload and on Cancel,
   * so the marker was always gone by the time the reloaded page read it and the
   * refresh-discard policy never fired at all. `pagehide` fires only when the
   * document is genuinely being unloaded, so "cancelled and stayed" never arms
   * in the first place — the guarantee is structural, not a race to win.
   *
   * `event.persisted` means a back/forward-cache freeze: the document is kept
   * alive and later restored WITHOUT a reload, so nothing would consume the
   * marker and it would sit armed for a later, unrelated page load. Skip it.
   *
   * NOTE (non-goal): in-app Angular route changes never unload the document, so
   * this does not fire for them — which is load-bearing. Navigating away and
   * confirming "Continue without saving" on `canDeactivateFilingGuard`'s dialog
   * must NOT discard anything in sibling tabs. Do not wire
   * `armUnloadDiscardIntent()` into `confirmLeave()` or the guard's confirm path.
   */
  @HostListener('window:pagehide', ['$event'])
  onPageHide(event: PageTransitionEvent): void {
    if (event.persisted) return;
    // Never arm the refresh-discard on a failed page: it would wipe an earlier
    // session's cached edits the user never saw. See `loadFailure`; the same
    // rationale scopes the Retry-hop bypass in `canDeactivateFilingGuard`.
    // Everything below still runs.
    if (this.loadFailure() === null && this.stateService.hasAnySheetModified()) {
      this.stateService.armUnloadDiscardIntent();
    }

    // Per-sheet abandonment, sender half. Capture the sheet BEFORE withdrawing
    // the probe, then withdraw, then broadcast — in that order.
    //
    // The ordering is the point. Withdrawing first means this dying tab cannot
    // answer its own release (BroadcastChannel filters self-echoes, but the
    // storage-event fallback and any future in-tab replay would not), and more
    // importantly it means a tab that has fired `pagehide` but is not yet
    // destroyed can no longer vouch for a sheet it is in the middle of
    // abandoning. That last case is a real observed failure mode of the
    // conversion-level liveness probe, and this is what keeps it from
    // reappearing here.
    //
    // Everything below must stay synchronous: the document is already being
    // torn down and no later task will run. `releaseSheet` broadcasts via
    // `BroadcastChannel.postMessage`, which is synchronous, so the message does
    // reach sibling tabs.
    const releasedSheet = this.currentSheet;
    this.stateService.clearMountedSheetProbe();
    if (releasedSheet) {
      this.stateService.releaseMountedSheetAcrossTabs(
        this.sheetTypeToStateKey[releasedSheet] ?? null
      );
    }
  }

  /**
   * Called by canDeactivateFilingGuard.
   * Opens a confirmation dialog and returns an Observable that emits true (leave) or false (stay).
   */
  confirmLeave(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.leavePageResolver = (value: boolean) => {
        observer.next(value);
        observer.complete();
      };
      this.isLeavePageDialogOpen.set(true);
    });
  }

  onLeavePageStay(): void {
    this.isLeavePageDialogOpen.set(false);
    this.leavePageResolver?.(false);
    this.leavePageResolver = null;
  }

  onLeavePageContinue(): void {
    this.isLeavePageDialogOpen.set(false);
    this.leavePageResolver?.(true);
    this.leavePageResolver = null;
  }

  /**
   * Check if current sheet has unsaved modifications
   */
  isCurrentSheetModified(): boolean {
    const stateKey = this.getStateKeyForSheet(
      this.currentSheet
    ) as keyof FilingFormState;
    return this.stateService.isSheetModified(stateKey);
  }

  /**
   * Revert current sheet to saved state
   */
  revertCurrentSheet(): void {
    const stateKey = this.getStateKeyForSheet(
      this.currentSheet
    ) as keyof FilingFormState;
    this.stateService.cancelSheetChanges(stateKey);

    // Trigger reload in the component
    const component = this.getCurrentComponent();
    if (component && typeof (component as any).loadSavedState === 'function') {
      (component as any).loadSavedState();
    }

    this.messageService.add({
      severity: 'info',
      summary: 'Changes Reverted',
      detail: `${this.getCurrentSheetLabel()} reverted to last saved state`,
      life: TOAST_DURATION_DEFAULT_MS,
    });
  }

  /**
   * Cancel changes and revert to original state (network-aware)
   */
  async cancelCurrentSheet(): Promise<void> {
    await this.stateService.cancelChanges();

    // Trigger reload in the component
    const component = this.getCurrentComponent();
    if (component && typeof (component as any).loadSavedState === 'function') {
      (component as any).loadSavedState();
    }
  }

  /**
   * Clear current sheet data
   */
  clearCurrentSheet(): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to clear all data in ${this.getCurrentSheetLabel()}? This action cannot be undone.`,
      header: 'Clear Sheet Data',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        const component = this.getCurrentComponent();
        if (component && typeof (component as any).clearForm === 'function') {
          (component as any).clearForm();
        }
      },
    });
  }

  /**
   * Submit current sheet data
   */
  submitCurrentSheet(): void {
    const component = this.getCurrentComponent();
    if (component && typeof (component as any).submitForm === 'function') {
      (component as any).submitForm();

      // Invalidate cached validation status for this sheet AND its cross-sheet
      // dependents. submitForm() is synchronous — after it runs, the sheet's data
      // may have changed, which can affect other sheets' equality validations.
      const stateKey = this.sheetTypeToStateKey[this.currentSheet] as keyof FilingFormState;
      if (stateKey) {
        const dependents = this.sheetDependencyService.getDependents(stateKey);
        if (dependents === null) {
          // Role-0: invalidate entire cache
          this.stateService.clearValidationCache();
        } else {
          for (const dep of dependents) {
            this.stateService.invalidateValidationCacheForSheet(dep as string);
          }
        }
      }
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Submit Not Available',
        detail: `Submit is not available for ${this.getCurrentSheetLabel()}`,
        life: TOAST_DURATION_DEFAULT_MS,
      });
    }
  }

  /**
   * Get current component instance
   */
  private getCurrentComponent(): any {
    switch (this.currentSheet) {
      case 'filing-info':
        return this.role1Component;
      case 'scope-of-filing':
        return this.role2Component;
      case 'stock-exchange':
        return this.role3Component;
      case 'directors-report':
        return this.role4Component;
      case 'statement-by-directors':
        return this.role5Component;
      case 'director-business-review':
        return this.role6Component;
      case 'auditors-report':
        return this.role7Component;
      case 'statement-of-financial-position':
        return this.role8Component;
      case 'sub-classification':
        return this.role9Component;
      case 'statement-of-financial-position-liquidity':
        return this.role10Component;
      case 'sub-classification-liquidity':
        return this.role11Component;
      case 'statement-of-profit-or-loss':
        return this.role12Component;
      case 'analysis-of-profit-or-loss':
        return this.role13Component;
      case 'statement-of-profit-or-loss-by-nature':
        return this.role14Component;
      case 'analysis-of-profit-or-loss-by-nature':
        return this.role15bComponent;
      case 'statement-of-comprehensive-income':
        return this.role15Component;
      case 'statement-of-comprehensive-income-before-tax':
        return this.role16Component;
      case 'statement-of-cash-flows-direct':
        return this.role17Component;
      case 'statement-of-cash-flows-indirect':
        return this.role18Component;
      case 'statement-of-changes-in-equity':
        return this.role19Component;
      case 'notes-corporate-information':
        return this.role20Component;
      case 'notes-accounting-policies':
        return this.role21Component;
      case 'notes-list-of-notes':
        return this.role22Component;
      case 'notes-issued-capital':
        return this.role23Component;
      case 'notes-related-party-transactions':
        return this.role24Component;
      default:
        return null;
    }
  }

  /**
   * Handle Role-0 (Filing Prerequisites) submission
   * Called when user clicks Submit button in Role-0 form
   */
  onRole0Submit(formData: any): void {
    // The hard block will automatically unlock other sheets because:
    // 1. Role-0 component already updated its validation state before emitting
    // 2. getEnabledSheets() checks isRole0Completed() which reads from state service
    // 3. The methodFieldsSubscription reactively updates filteredSheets
    //
    // NOTE: navigation happens ONLY in the success branch below. A previous
    // unconditional `setTimeout(navigate, 2000)` in first-time mode fired even
    // on a FAILED submit, throwing the user off the form despite the error
    // toast. Navigating on success only keeps the user on the form to retry,
    // matching the Submit-for-Validation behaviour.

    // Append conversionId into form.
    formData.ConversionId = this.conversionId();

    // Both non-form keys are added here rather than inside the sheet, so the
    // sheets keep emitting pure getRawValue() output.
    //
    // OMITTED, never null, when nothing is selected: an absent field means
    // "use the configured Conversion:Engine" server-side, which makes the
    // request byte-identical to the pre-feature one. Sending an explicit null
    // would be a different request, and the flag-off path is the path
    // production runs on first.
    const role0Engine = this.role0Component?.selectedEngine ?? null;
    if (role0Engine !== null) {
      formData.ConversionEngine = role0Engine;
    }

    // Already integrated with backend (can ask keyyung if have problem on this part)
    // Send to backend API
    this.formSubmitService
      .submitXBRLPhaseTwoForm(formData, this.fileName())
      .pipe(
        tap({
          next: (response) => {
            if (!response.status) {
              throw new Error(response.errors.toString());
            }

            // Backend persisted the submission — only now commit local state
            // in the sheet (markAsPristine + commitSheetAsOriginal).
            this.role0Component?.onSubmitSucceeded();

            // Clear entire validation cache — Role-0 submission can change
            // which sheets are enabled and overwrite other sheets' data.
            this.stateService.clearValidationCache();

            // The submit is persisted; a leave prompt here can only mislead.
            // See PHASE2_SUBMIT_NAV_STATE.
            this.router.navigate(['/mbrs/xbrl-files'], {
              state: {
                [PHASE2_SUBMIT_NAV_STATE]: true,
                toast: {
                  key: 'process_mbrs_data',
                  severity: 'custom-info',
                  summary: `We are converting <b>${response.data}</b> and will notify you when it's ready.`,
                  life: TOAST_DURATION_LONG_MS,
                },
              },
            });
          },
        })
      )
      .subscribe({
        error: () => {
          // Any non-200 (or a 200 with status:false) — the FRONTEND owns the
          // error message; the backend body is intentionally ignored. Re-enable
          // the Submit button, keep the user on the form (no commit, no
          // navigation), and show a single fixed toast.
          this.role0Component?.onSubmitFailed();
          this.messageService.add({
            key: 'process_mbrs_data',
            severity: 'custom-error',
            summary: 'Failed to submit for validation. Please try again later.',
            life: TOAST_DURATION_LONG_MS,
          });
          return;
        },
      });
  }

  /**
   * Handle Role-1 (Filing Information, Phase 2) submission.
   * Mirrors onRole0Submit: appends the conversionId, calls the same
   * Phase-2 backend endpoint, clears the validation cache, and navigates
   * back to the XBRL files page on success. Role-1 is the Phase-2 editable
   * surface for filing-info, so the persistence path is identical to role0.
   */
  onRole1Submit(formData: any): void {
    formData.ConversionId = this.conversionId();

    // Mirrors onRole0Submit — see the comment there for why this is omitted
    // rather than nulled.
    const role1Engine = this.role1Component?.selectedEngine ?? null;
    if (role1Engine !== null) {
      formData.ConversionEngine = role1Engine;
    }

    // Captured BEFORE the request: onSubmitSucceeded() commits the sheet, which
    // clears the flag. Drives the other-sheet discard below.
    const wasFilingInfoModified = this.stateService.isSheetModified(
      SHEET_KEYS.filingInfo
    );

    this.formSubmitService
      .submitXBRLPhaseTwoForm(formData, this.fileName())
      .pipe(
        tap({
          next: (response) => {
            if (!response.status) {
              throw new Error(response.errors.toString());
            }

            // Backend persisted the submission — only now commit local state
            // in the sheet (markAsPristine + commitSheetAsOriginal).
            this.role1Component?.onSubmitSucceeded();

            // Tell sibling tabs on this conversion to leave for the listing. Any
            // tab held by the cross-tab Filing Information block must navigate
            // rather than watch the popup silently close — the popup's own copy
            // promises "This tab will move to the XBRL File page automatically".
            // Broadcast FIRST: this goes out via notifyImmediate (undebounced)
            // while the modifiedState notify that would close the popup is
            // debounced, so the redirect reliably wins the race. The receiver is
            // the container's own conversionResetRequested$ subscriber.
            this.stateService.requestConversionResetAcrossTabs();

            // P.O. ruling (2026-08-17): an edited Filing Information re-submits
            // the conversion, and the backend re-runs LLM extraction over EVERY
            // sheet — so any local edit on another sheet is about to be
            // overwritten and must not survive. `discardModifiedChanges()` runs
            // AFTER `onSubmitSucceeded()` on purpose: that call has just
            // promoted filingInfo's modified slice to original, so reverting
            // modified→original discards every OTHER sheet's edits while
            // leaving the just-submitted Filing Information values intact.
            // Role-0 needs no equivalent — its own submit path already wipes
            // the other sheets via `clearAllOtherRolesData()`.
            //
            // Gated on the sheet actually having changed, so a no-change
            // re-submit can never silently throw away work the user did
            // elsewhere. Since the P.O.'s 2026-08-17 ruling role1's Submit
            // gate blocks a pristine form, so this is defence-in-depth rather
            // than a live button path — keep it: it is the only thing standing
            // between a non-button caller and another sheet's edits.
            if (wasFilingInfoModified) {
              this.stateService.discardModifiedChanges();
            }

            // Clear entire validation cache — Role-1 submission can change
            // which sheets are enabled and overwrite other sheets' data.
            this.stateService.clearValidationCache();

            // The submit is persisted; a leave prompt here can only mislead.
            // See PHASE2_SUBMIT_NAV_STATE.
            this.router.navigate(['/mbrs/xbrl-files'], {
              state: {
                [PHASE2_SUBMIT_NAV_STATE]: true,
                toast: {
                  key: 'process_mbrs_data',
                  severity: 'custom-info',
                  summary: `We are converting <b>${response.data}</b> and will notify you when it's ready.`,
                  life: TOAST_DURATION_LONG_MS,
                },
              },
            });
          },
        })
      )
      .subscribe({
        error: () => {
          // Any non-200 (or a 200 with status:false) — the FRONTEND owns the
          // error message; the backend body is intentionally ignored. Re-enable
          // the Submit button, keep the user on the form (no commit, no
          // navigation), and show a single fixed toast.
          this.role1Component?.onSubmitFailed();
          this.messageService.add({
            key: 'process_mbrs_data',
            severity: 'custom-error',
            summary: 'Failed to submit for validation. Please try again later.',
            life: TOAST_DURATION_LONG_MS,
          });
          return;
        },
      });
  }

  /**
   * Update Role-21 (notes-accounting-policies) description based on accounting standard
   */
  private updateRole21Description(accountingStandard: string): void {
    const role21Sheet = this.sheets.find(
      (sheet) => sheet.value === 'notes-accounting-policies'
    );

    if (role21Sheet) {
      if (
        accountingStandard === 'Malaysian Private Entities Reporting Standards'
      ) {
        // MPERS: "significant" accounting policies
        role21Sheet.description =
          '[720000] Summary of significant accounting policies';
      } else {
        // MFRS (default): "material" accounting policies
        role21Sheet.description =
          '[720000] Summary of material accounting policies';
      }
    }
  }

  /**
   * Fetch all MBRS data from backend
   * Uses hardcoded ConversionId for now
   */
  private fetchAllMbrsData(): void {
    const conversionId = this.conversionId()!;

    // Open the container's load trace. Steps 1 and 2 are already facts by the
    // time we get here — this method is only ever called with a conversion id,
    // from inside `whenHydrated().finally()` — so they are recorded up front
    // rather than left to imply a failure that did not happen.
    const trace = this.loadTrace.beginContainerLoad(conversionId, () =>
      this.resolveLoadFailureSheetScope(),
    );
    trace.mark(CONTAINER_LOAD_STEPS.conversionIdResolved);
    trace.mark(CONTAINER_LOAD_STEPS.persistedStateHydrated);

    // Clear stale status locks from a previous visit to this conversion.
    // The service is a singleton — statusLockedSheets persists across navigations.
    // Without clearing, updateSheetLevelStatus() calls during data loading
    // (e.g., line marking filingPrerequisites as valid) would be silently blocked,
    // causing isRole0Completed() to return false and getEnabledSheets() to return
    // only ['filing-prerequisites'].
    this.stateService.unlockAllSheetStatuses();

    // Flag MBRS-data hydration as in progress. Must be called synchronously
    // here (before the async fetch kicks off) so role components mounting
    // immediately see the hydration signal and can gate their form reveal
    // via `waitForMbrsHydration()`. Cleared in the finalize() below —
    // symmetric with every `endMbrsHydration()` so awaiters never deadlock.
    this.stateService.beginMbrsHydration();

    // Surface the load-progress overlay. begin() is idempotent-safe: if
    // Router's NavigationStart already opened the window, this resets the
    // phase label to the hydration-fetch message.
    this.loadProgress.begin('Loading conversion data...');
    this.loadProgress.reportPhase('Loading conversion data...');

    // Mark the start of an initial-hydration window. While set, every
    // saveOriginalState/saveModifiedState that funnels through the state
    // service short-circuits its cross-tab notify (see `notifyCrossTab`'s
    // `isHydrating` guard). This prevents the receiver tab from being
    // spammed with broadcasts for state it already has identical to its
    // own localStorage. Cleared in finalize() so it ALWAYS clears on both
    // success AND error paths.
    this.stateService.beginHydration();

    // The data fetch waits for the scenarios file (it decides which sheets a
    // filing hides). A scenarios failure surfaces on the `error:` callback below
    // as a typed `SchemaAssetError` and is classified by `handleFetchException`;
    // `finalize` still runs, so the hydration flags are released on that path too.
    from(this.awaitScenarios())
      .pipe(
        tap(() => trace.mark(CONTAINER_LOAD_STEPS.scenariosLoaded)),
        switchMap(() => this.retrieveMbrsDataService.fetchMbrsData(conversionId, '')), // '' = all sheets
        finalize(() => {
        this.stateService.endMbrsHydration();
        this.mbrsLoadSettled.set(true);
        // CRITICAL: Recalculate filtered sheets after loading completes
        // This ensures dropdown shows correct options whether API returned data or not.
        // enforcePhaseGatedSheet() also snaps `currentSheet` back to a Phase-1
        // legal value if the saved-sheet restore placed us on a Phase-2-only
        // sheet.
        this.enforcePhaseGatedSheet();
        this.cdr.detectChanges();
        // Always clear the hydration guard — runs on success, error, and
        // unsubscribe paths so the flag never leaks across the boundary.
        this.stateService.endHydration();
        // NOTE: the overlay is NOT torn down here. The populate block below
        // is only the *data hydration* stage — the real wait on first load
        // is the background validation cycle that runs each sheet through
        // its component. The overlay must stay up until either:
        //   - the FULL CACHE HIT branch applies cached statuses, OR
        //   - runBackgroundValidationCycle() finishes, OR
        //   - the error branch below fires.
        }),
      )
      .subscribe({
        next: (response) => {
          // Step 3 means "a response reached the browser", so it must NOT be
          // marked for a NET failure — there was no response. Marking it
          // unconditionally would log `steps reached: [1,2,3]` beside a NET
          // code, a ledger that claims a response arrived while its class
          // says none did; a record that contradicts itself is worse than a
          // vague message. Every other
          // classified failure DID come back over the wire, so they keep it.
          if (response.failure !== 'NET') {
            trace.mark(CONTAINER_LOAD_STEPS.responseReceived);
          }

          // A classified failure means the service already turned a transport
          // fault into an empty placeholder. Report it, then either redirect
          // (PERM/RESP/AUTH) or stay on the page with the failure panel, and stop.
          // Running the populate pipeline over `{}` is what used to make a
          // failed load indistinguishable from a conversion with no data.
          if (response.failure) {
            trace.fail(response.failure, {
              statusCode: response.statusCode,
              errorCode: response.errorCode,
              errors: response.errors,
            });
            this.loadProgress.end();
            this.handleContainerLoadFailure(response.failure, trace.failureSummary);
            return;
          }

          void this.applyMbrsDataResponse(response, trace).catch((error) =>
            this.handleApplyFailure(trace, error),
          );
        },
        error: (error) => this.handleFetchException(trace, error),
      });
  }

  /**
   * BUILD exit: `applyMbrsDataResponse` threw after the response was accepted.
   *
   * The `.catch` that calls this is load-bearing. The `next` handler was
   * previously `async` and passed directly to `subscribe`, but RxJS does not
   * route a returned promise's rejection to the `error` callback — so
   * anything that threw after the first `await` became an unhandled
   * rejection: no toast, no console error, and the loading overlay left up
   * forever because `loadProgress.end()` was never reached.
   *
   * The failure panel is shown ONLY if the trace never reached
   * `CONTAINER_LOAD_STEPS.sheetsPopulated`. Before that step the sheets are
   * partially populated, and a half-built form must not be editable or
   * submittable. After it the data is complete (e.g. a throw while resolving
   * the landing sheet), so the user keeps the working form and only the
   * toast reports the failure.
   */
  private handleApplyFailure(trace: LoadTrace, error: unknown): void {
    trace.fail('BUILD', error);
    this.loadProgress.end();
    if (!trace.stepsReached.includes(CONTAINER_LOAD_STEPS.sheetsPopulated)) {
      this.handleContainerLoadFailure('BUILD', trace.failureSummary);
    }
  }

  /**
   * PARSE exit: the `fetchMbrsData` subscribe `error:` callback.
   *
   * Reachable ONLY for an exception thrown inside the service's `map` — its
   * `catchError` sits upstream of that `map`, so a transport failure never
   * arrives here at all. It comes through `next` with `failure` set instead.
   * What lands here is a response whose body could not be reshaped, which is
   * a parse-class fault. No data reached the page, so the failure panel is
   * the only honest view and is always shown.
   */
  private handleFetchException(trace: LoadTrace, error: unknown): void {
    // A typed error here is `scenarios.config.json` failing (ASSET / PARSE / NET):
    // the data fetch waits for it, so its rejection arrives on the same callback.
    // Anything else is the body-reshaping fault described above — PARSE.
    const failure = error instanceof SchemaAssetError ? error.failure : 'PARSE';
    trace.fail(failure, error);
    this.loadProgress.end();
    this.handleContainerLoadFailure(failure, trace.failureSummary);
  }

  /**
   * Where to send the user after a `PERM` (403) `fetchMbrsData` failure.
   *
   * `PERM` (P.O., 2026-10-05): a 403 here means "not yours to see", not a
   * transport or server fault — bouncing to the dashboard is a worse landing
   * than back to the page the user opened this conversion from.
   *
   * Deliberately kept OUT of `RetrieveMbrsDataService.fetchMbrsData` — that
   * service classifies but must never navigate, because
   * `ConversionValidatorService.loadConversionSheetData` calls the exact same
   * method for its headless background validation cycle. A redirect living
   * inside the shared service double-fired when both callers hit the same
   * 403 on the same conversion, sending the user back an extra page.
   *
   * **`PERM` navigates FORWARD to the previous URL, never `Location.back()`.**
   * `Location.back()` was tried first and abandoned: going BACK through
   * browser history can restore the destination page from the back-forward
   * cache (bfcache) instead of performing any navigation at all — confirmed
   * directly (2026-10-05, DevTools console: `Page entered Back-Forward
   * Cache` on every open SignalR hub) on a tab with a long history. A
   * bfcache restore freezes the whole page — Angular included — the instant
   * the user leaves it, and later unfreezes the exact same frozen state with
   * **no script running during the transition at all**. No toast, no
   * console log, nothing this app owns can react to it. Pushing a BRAND NEW
   * history entry at the tracked previous URL via `router.navigateByUrl()`
   * has no such failure mode: a freshly pushed entry has never been frozen,
   * so there is nothing for the browser to restore instead of actually
   * navigating. `PreviousUrlTrackerService` is what supplies that URL — see
   * its own docblock for why it must be instantiated eagerly.
   *
   * **A fallback for a tab with no previous URL to return to.** A conversion
   * link opened fresh — pasted into a new tab, or the only URL that tab has
   * ever shown — has no prior navigation for `PreviousUrlTrackerService` to
   * have recorded. `window.history.length` independently detects the same
   * case: confirmed by hand (2026-10-05), an EMPTY new tab with the link
   * pasted in reads `2`, not `1` — the browser's own "new tab" page is
   * itself an entry — so `<= 2` is the correct threshold, not `<= 1`. The
   * XBRL Files listing is the deliberate fallback DESTINATION rather than
   * the dashboard — a blocked conversion's natural "home" is the list it
   * would have been opened from, matching `PERM`'s whole rationale above.
   */
  private redirectOnPermissionFailure(): void {
    const previousUrl = this.previousUrlTracker.getPreviousUrl();
    if (window.history.length <= 2 || !previousUrl) {
      this.redirectToConversionListing();
    } else {
      this.router.navigateByUrl(previousUrl);
    }
  }

  /**
   * Leave a conversion that cannot be opened for the XBRL Files listing — the
   * list it would be opened from. `RESP`'s destination, and `PERM`'s fallback.
   *
   * `RESP` (P.O., 2026-10-12; named `REQ` until it was retired 2026-10-14)
   * goes here ALWAYS, not to the previous URL as `PERM` does:
   * `retrieve-mbrs-data` answers 4xx when the backend rejects the conversion
   * itself (404 `CONVERSION_NOT_FOUND` on the current backend; an unknown or
   * malformed id used to read "Conversion not found!" as a 400), so there is nothing on this page worth retrying and the listing
   * is where a valid one can be picked. The toast survives the navigation:
   * `MainLayoutComponent` renders it, see `SheetLoadTraceService.failures$`.
   */
  private redirectToConversionListing(): void {
    this.router.navigate([XBRL_FILES_URL]);
  }

  /**
   * Route a classified container-scope load failure (P.O., 2026-10-05):
   * - PERM leaves for where the user came from — see `redirectOnPermissionFailure`.
   * - RESP leaves for the XBRL Files listing (P.O., 2026-10-12) — see
   *   `redirectToConversionListing`.
   * - A silent class (AUTH) asks whether the session really ended — see
   *   `leaveForLoginIfSessionEnded`. It never navigates on its own.
   * - Everything else STAYS on this page with the failure panel.
   *
   * `summary` is `LoadTrace.failureSummary` — null when the trace had already
   * closed (e.g. the stall watchdog fired first), in which case the class's
   * own sentence is shown without a code.
   */
  private handleContainerLoadFailure(failure: LoadFailureClass, summary: string | null): void {
    if (failure === 'PERM') {
      this.redirectOnPermissionFailure();
      return;
    }
    if (failure === 'RESP') {
      this.redirectToConversionListing();
      return;
    }
    if (SILENT_FAILURE_CLASSES.has(failure)) {
      void this.leaveForLoginIfSessionEnded(failure);
      return;
    }
    this.loadFailure.set({ failure, summary: summary ?? LOAD_FAILURE_COPY[failure] });
  }

  /**
   * AUTH exit: go to the login page only if the session really ended, and
   * go there through `forceLogout` — never through a navigation of our own.
   *
   * ## Why this no longer navigates to the dashboard
   *
   * It used to call `router.navigate(['/mbrs/dashboard'])`. By then the 401
   * had already made `sessionExpiryInterceptor` call `forceLogout`, whose own
   * navigation to `/auth/login` carries `state: { authReason }`. Angular
   * cancels a navigation still in progress when a newer one starts, so the
   * dashboard trip REPLACED it, and the user reached login a third hop later
   * via `isAuthenticatedGuard`'s redirect — login, dashboard, login. That
   * redirect carries no `authReason`. The marker is what tells
   * `redirectIfAuthenticatedGuard` the logout is authoritative; without it,
   * that guard re-asks `is-authenticated`, which can still report a
   * just-superseded session as live for a short window and send the user
   * back into the app (AS-569, "the logout is silently undone").
   *
   * ## What it does instead
   *
   * `SessionGateService.confirmSessionBeforeLeaving` is the one seam that
   * already answers "did the session end?":
   *
   * - The interceptor's forced logout already ran (the normal case): the
   *   `isAuthenticated` signal is `false`, the gate answers `false` with no
   *   request, and `forceLogout`'s own navigation reaches login untouched.
   * - The interceptor did not act (a 401 it gate-skipped): the gate asks
   *   `is-authenticated`; `false` makes it call `forceLogout` with this page's
   *   load-failure scope, so the code still reads `MBRS-S<role>-AUTH-…`.
   * - The session is still live, or the check could not answer: there is
   *   nowhere honest to send the user, so they stay on the failure panel.
   */
  private async leaveForLoginIfSessionEnded(failure: LoadFailureClass): Promise<void> {
    const sessionLive = await this.sessionGate.confirmSessionBeforeLeaving(
      this.loadFailureDiagnosticScope(),
    );
    if (!sessionLive)
      return;
    this.loadFailure.set({ failure, summary: this.loadFailureAuthSummary() });
  }

  /**
   * Retry a failed load by re-creating this page at its own URL: hop to the
   * empty `ROUTE_RELOAD_HOP_URL` without touching the address bar, then
   * navigate back, replacing the history entry. Angular reuses a component on
   * a same-URL navigation, so leaving the route is what makes `ngOnInit` — and
   * with it a fresh load trace, toast gate and `loadFailure` — run again. A
   * refused hop (a guard said no) leaves the page as it is.
   */
  retryLoad(): void {
    const filingUrl = this.router.url;
    void this.router
      .navigateByUrl(ROUTE_RELOAD_HOP_URL, { skipLocationChange: true })
      .then((hopped) => {
        if (hopped) {
          void this.router.navigateByUrl(filingUrl, { replaceUrl: true });
        }
      });
  }

  /**
   * Classify and toast a failed `xbrl-data-listing` (FILING_PREVIEW) read —
   * the secondary call this container makes alongside `retrieve-mbrs-data`
   * to populate `fileName`/`conversionStatus`/a defensive `conversionPhase`.
   *
   * Extracted to its own method, same reasoning as `handleContainerLoadFailure`:
   * a prototype-host spec can drive it directly without mounting the whole
   * component. Never navigates — a missed preview read does not block the
   * rest of the filing form, so `fileName`/`conversionStatus` simply keep
   * whatever they held before.
   *
   * **`NET` is logged but never toasted here (P.O., 2026-10-06).** `NET` means
   * the browser could not reach the server, so it hits every request in
   * flight — including this container's own `retrieve-mbrs-data` load, which
   * already reports it (its `MBRS-S<role>-NET` toast via `SheetLoadTraceService`
   * plus the stay-on-page failure panel). Toasting here as well showed the
   * user two "Connection lost" toasts for one outage. The two requests can
   * fail in either order, so this is a rule on the class, not a check of the
   * other load's outcome. `buildPageLoadFailureNotice` still writes the
   * console record.
   */
  private handleXbrlPreviewFailure(error: unknown): void {
    const notice = buildPageLoadFailureNotice('FILING_PREVIEW', error);
    if (notice.failure === 'NET') {
      return;
    }
    if (notice.silent) {
      // AUTH: staged under the SHEET's code, not `FILING_PREVIEW`. This read
      // runs in parallel with `retrieve-mbrs-data`, and one expired session
      // fails both; the login page shows whichever was staged last, so two
      // different codes made one incident report as either. Both now stage
      // the identical `MBRS-S<role>-AUTH-<conversion>`, so the order no
      // longer matters. The console line above keeps `FILING_PREVIEW`.
      this.authFailureNotice.stage(this.loadFailureAuthSummary());
    } else if (this.loadFailureToastGate.shouldToast(notice.failure)) {
      this.messageService.add({
        key: 'process_mbrs_data',
        severity: 'error',
        summary: notice.summary,
        life: DIAGNOSTIC_TOAST_LIFE_MS,
      });
    }
  }

  /**
   * Apply a successful `retrieve-mbrs-data` response: populate every sheet,
   * resolve the validation plan, and land on the right sheet.
   *
   * Extracted from the `subscribe` handler so that (a) its rejections are
   * catchable — an `async` function handed to `subscribe` swallows them — and
   * (b) it can record its progress on the load trace, which is what lets a
   * failure here name the step it died on rather than reporting a bare
   * "Failed to load MBRS data".
   *
   * @param response The accepted response. Callers must have already handled a
   *                 `response.failure`; this method assumes success.
   * @param trace    The container's live load trace.
   */
  private async applyMbrsDataResponse(
    response: RetrievedMbrsData,
    trace: LoadTrace,
  ): Promise<void> {
    // A successful response with no sheet data is a NORMAL blank conversion, not a
    // failure (P.O./developer ruling, 2026-10-26): the service already returns an
    // object for every success, and the backend never answers success with zero
    // rows. The `?? {}` keeps that true even for a hand-built response, so the
    // guard below is always entered and the loading overlay can never be left up.
    const data = response?.sheetData ?? {};
    const sheetStatuses = response?.sheetStatuses;
    const snapshot = response?.snapshot;

    if (snapshot) {
      this.population.populateSnapshotFromMbrsData(snapshot);
    }
    // The retrieve-mbrs-data endpoint is the authoritative source for
    // conversion phase — it is returned alongside sheet data on every
    // filing page load. Only overwrite if the backend actually supplied
    // a value so pre-rollout environments keep falling through to the
    // `typeOfSubmission` filter instead of being forced into Phase 2.
    if (response?.phase !== undefined) {
      this.conversionPhase.set(response.phase);
      this.stateService.setConversionPhase(response.phase);
    }
    // retrieve-mbrs-data is the authoritative phase source — once it's
    // landed (even when the response omits phase and we fall through to
    // Phase 1 defaults), wire up the phase-aware filing-info
    // subscriptions. Idempotent with the queryParams-listing path.
    this.setupActiveFilingInfoSubscriptions();
    if (data) {
      trace.mark(CONTAINER_LOAD_STEPS.responseAccepted);
      this.allSheetsData.set(data);

      // Start API data loading phase - skip edited marks during populate
      this.stateService.startAPIDataLoading();

      // Start batch mode to defer localStorage writes until all populates complete
      this.stateService.startBatchMode();

      // Per-sheet progress reporting for the full-page load overlay.
      // Counter is closure-local so it survives across populate calls
      // without a class-level field. Total matches the number of
      // populate* calls below — keep these in sync if sheets are
      // added/removed from the hydration sequence.
      //
      // PR-D phase-aware hydration: only ONE of filing-prerequisites
      // (Phase 1) or filing-info (Phase 2) is populated — the inactive
      // slot is left empty. This keeps a single source of truth per
      // phase and avoids the stale-mirror drift that happens when both
      // slots are written from the same backend payload but only one
      // of them receives user edits.
      // Populate the ACTIVE filing-info source for the current phase.
      // Phase 1 (or unknown/null) → filingPrerequisites (role0);
      // Phase 2 → filingInfo (role1). The inactive slot stays empty so
      // any consumer that reads the wrong one fails loudly instead of
      // returning stale backend data.
      const isPhase2 = this.conversionPhase() === ConversionPhaseEnum.Phase2;

      // Phase-aware step total: Phase 2 hydrates all 19 sheets; Phase 1
      // reveals only filing-prerequisites (one populate), so a hardcoded
      // 19 would freeze the overlay at "1 / 19". Keep this in sync with
      // the number of reportStep() calls in each branch below.
      const totalHydrationSteps = isPhase2 ? 19 : 1;
      let hydrationStep = 0;
      const reportStep = (value: SheetType) => {
        const label = this.resolveSheetDisplayLabel(value);
        this.loadProgress.reportSheet(label, ++hydrationStep, totalHydrationSteps);
      };

      if (isPhase2) {
        reportStep('filing-info');
        this.population.populateFilingInfoFromMbrsData(data);

        // Populate ScopeOfFiling immediately
        reportStep('scope-of-filing');
        this.population.populateScopeOfFilingFromMbrsData(data);

        // Populate DirectorBusinessReview immediately
        reportStep('director-business-review');
        this.population.populateDirectorBusinessReviewFromMbrsData(data);

        // Populate DirectorsReport immediately
        reportStep('directors-report');
        this.population.populateDirectorsReportFromMbrsData(data);

        // Populate StatementByDirectors immediately
        reportStep('statement-by-directors');
        this.population.populateStatementByDirectorsFromMbrsData(data);

        // Populate StockExchange immediately
        reportStep('stock-exchange');
        this.population.populateStockExchangeFromMbrsData(data);

        // Populate AuditorsReport immediately
        reportStep('auditors-report');
        this.population.populateAuditorsReportFromMbrsData(data);

        // Populate NotesCorporateInformation immediately
        reportStep('notes-corporate-information');
        this.population.populateNotesCorporateInformationFromMbrsData(data);

        // Populate NotesAccountingPolicies immediately
        reportStep('notes-accounting-policies');
        this.population.populateNotesAccountingPoliciesFromMbrsData(data);

        // Populate NotesListOfNotes immediately
        reportStep('notes-list-of-notes');
        this.population.populateNotesListOfNotesFromMbrsData(data);

        // Populate NotesIssuedCapital immediately
        reportStep('notes-issued-capital');
        this.population.populateNotesIssuedCapitalFromMbrsData(data);

        // Populate NotesRelatedPartyTransactions immediately
        reportStep('notes-related-party-transactions');
        this.population.populateNotesRelatedPartyTransactionsFromMbrsData(data);

        // Populate StatementOfFinancialPosition immediately (SOFP-CuNonCu)
        reportStep('statement-of-financial-position');
        this.population.populateStatementOfFinancialPositionFromMbrsData(data);

        // Populate SubClassification immediately (SOFP-Sub-CuNonCu)
        reportStep('sub-classification');
        this.population.populateSubClassificationFromMbrsData(data);

        // Populate StatementOfProfitOrLoss immediately
        reportStep('statement-of-profit-or-loss');
        this.population.populateStatementOfProfitOrLossFromMbrsData(data);

        // Populate StatementOfProfitOrLossAnalysisFunction immediately
        reportStep('analysis-of-profit-or-loss');
        this.population.populateStatementOfProfitOrLossAnalysisFunctionFromMbrsData(
          data
        );

        // Populate StatementOfComprehensiveIncome immediately
        reportStep('statement-of-comprehensive-income');
        this.population.populateStatementOfComprehensiveIncomeFromMbrsData(data);

        // Populate StatementOfCashFlowsIndirect immediately
        reportStep('statement-of-cash-flows-indirect');
        this.population.populateStatementOfCashFlowsIndirectFromMbrsData(data);

        // Populate StatementOfChangesInEquity immediately
        reportStep('statement-of-changes-in-equity');
        this.population.populateStatementOfChangesInEquityFromMbrsData(data);
      } else {
        reportStep('filing-prerequisites');
        this.population.populateFilingPrerequisiteFromMbrsData(data);

        // Same rationale as above, keyed to the Phase 1 slot.
        this.stateService.updateSheetLevelStatus(SHEET_KEYS.filingPrerequisites, {
          hasErrors: false,
          hasSuggestedChanges: false,
        });
      }

      trace.mark(CONTAINER_LOAD_STEPS.sheetsPopulated);

      // Populate stage done — subsequent progress (per-sheet
      // validation) is reported from runBackgroundValidationCycle.
      // If that cycle is skipped (full cache hit below), the cache
      // branch is responsible for tearing the overlay down.
      this.loadProgress.reportPhase('Preparing validation...');

      // CRITICAL: Clear the hydration flag BEFORE computing enabled sheets.
      // getEnabledSheets() gates on `isHydratingMbrsData()` — while true
      // it only returns filing-prerequisites, causing
      // markAllSheetsValidationPending to receive an almost-empty set.
      // The finalize() callback also clears this flag, but it runs
      // AFTER the next handler completes, which is too late.
      this.stateService.endMbrsHydration();

      // The active filing-info source (filing-prerequisites in Phase 1,
      // filing-info in Phase 2) is a frontend-only gate sheet — it is NOT
      // included in API sheetStatuses, so nothing else clears its seeded
      // `hasErrors: true`. Establish it BEFORE getEnabledSheets(): that call
      // routes through SheetApplicabilityService.computeEnabledSheets, whose
      // first act is a hard block to the gate sheet alone unless
      // isActiveFilingInfoCompleted() holds — and that predicate reads this
      // very flag. Seeding it later (it used to live in the partial/miss
      // branch below) left `enabledStateKeys` as the lone gate sheet, which
      // made the full-cache-hit test trivially satisfiable and narrowed the
      // statuses actually applied down to that one sheet, painting a red
      // cross on all 25 others. See resolveValidationCachePlan.
      const activeFilingInfoKey = this.stateService.getActiveFilingInfoKey();
      const filingInfoData = this.stateService.getSheetData(activeFilingInfoKey);
      if (filingInfoData && Object.keys(filingInfoData).length > 0) {
        this.stateService.updateSheetLevelStatus(activeFilingInfoKey, {
          hasErrors: false,
          hasSuggestedChanges: false,
        });
        this.stateService.markSheetValidationComplete(activeFilingInfoKey);
      }

      this.filteredSheets = this.getEnabledSheets();

      // Check for cached validation statuses from a previous page load.
      // Three possible outcomes:
      //   FULL HIT    — cache is complete & covers every enabled sheet → skip cycle
      //   PARTIAL HIT — cache exists but is missing some enabled sheets → resume cycle
      //                 for the missing ones only (e.g., interrupted prior run,
      //                 or a cross-sheet dependent was invalidated on submit)
      //   MISS        — no cache → run full background validation cycle
      const cachedEntry = this.stateService.loadValidationCache();

      // Compute enabled state keys once; used by every branch below.
      const enabledStateKeys = this.filteredSheets
        .map(sheet => this.sheetTypeToStateKey[sheet.value] as string)
        .filter(Boolean);
      const currentStateKey = this.sheetTypeToStateKey[this.currentSheet] as string;

      const { filteredCached, missingStateKeys, isFullCacheHit } =
        this.resolveValidationCachePlan(
          cachedEntry,
          enabledStateKeys,
          activeFilingInfoKey as string,
        );

      if (isFullCacheHit && filteredCached) {
        // FULL CACHE HIT: Apply cached statuses and skip the background validation cycle.
        // All sheet statuses are known from the previous validation run.
        this.stateService.batchUpdateSheetLevelStatus(filteredCached);
        // The cache covered every enabled sheet, so this tab now holds a full
        // cover too — keep the entry's `complete` flag alive through the
        // debounced persist that the batch above just scheduled.
        this.stateService.markFullValidationPublished(Object.keys(filteredCached));
        // No pending flags were set, but clear defensively in case another path set them
        this.stateService.clearAllPendingValidation();
        this.validationCycleCompleted = true;

        // Lock all sheet statuses to match the post-validation-cycle state.
        // This prevents late setTimeout callbacks from the currently-visible
        // component's init from overwriting cached statuses.
        for (const key of enabledStateKeys) {
          this.stateService.lockSheetStatus(key);
        }
        // Unlock the current sheet so live validation from the active component works
        if (currentStateKey) {
          this.stateService.unlockSheetStatus(currentStateKey);
        }

        // Fast path — no background validation cycle will run, so
        // tear down the overlay here. The 150ms grace delay in the
        // overlay component hides the flash on rapid re-entries.
        this.loadProgress.end();
      } else {
        // PARTIAL HIT or MISS: Mark sheets that still need validation as pending
        // (shows skeleton loaders on only those sheets) and run the cycle.
        this.stateService.markAllSheetsValidationPending(missingStateKeys);

        // PARTIAL HIT path: apply the cached subset so already-validated
        // sheets paint their status immediately (no skeleton flash). We do
        // NOT lock them here. `runBackgroundValidationCycle()` below recomputes
        // EVERY Phase-2 sheet synchronously over the freshly-hydrated state and
        // then locks them with those authoritative verdicts. Locking the cached
        // values first would DISCARD that recompute — `updateSheetLevelStatus`
        // early-returns on a locked sheet — so a STALE or wrong cache entry
        // (e.g. one written before a cross-sheet operand was reconciled) would
        // stick on the dropdown until the user opened the sheet: the reported
        // "X that flips to a tick on visit" bug. Leaving them unlocked lets the
        // cycle overwrite-then-lock in the same tick, so the cache can never
        // outvote a fresh recompute. The cycle's own lock (it locks all sheets
        // after publishing) still blocks the late setTimeout writes the lock was
        // there to stop — the protection just moves from here to the cycle.
        if (filteredCached && Object.keys(filteredCached).length > 0) {
          this.stateService.batchUpdateSheetLevelStatus(filteredCached);
        }

        // Apply backend-authoritative sheet validation statuses from the API response,
        // EXCLUDING any sheets already restored from the local cache. Cached statuses
        // reflect the user's most recent local validation run (which may include
        // unsaved edits) and are fresher than the API snapshot on a partial resume.
        if (sheetStatuses) {
          const excludeCachedKeys = filteredCached
            ? new Set(Object.keys(filteredCached))
            : undefined;
          // Scope to the sheets this filing actually uses. The backend's map
          // is NOT applicability-filtered, and these keys are now registered
          // as reported (so they reach the persisted cache) and merged (so the
          // cycle cannot displace them) — an inapplicable sheet's `Review`
          // would otherwise drive ConversionStatusResolver's terminal verdict.
          this.applyApiSheetStatuses(
            sheetStatuses,
            excludeCachedKeys,
            new Set(enabledStateKeys),
          );
        }

        // Run the orchestrator once over the loaded conversion's
        // FilingFormState. Pure validators are synchronous; the editor
        // no longer needs to mount each sheet to populate the dropdown
        // status icons. See runBackgroundValidationCycle's docblock for
        // the Phase 2F migration notes.
        this.runBackgroundValidationCycle();
      }

      trace.mark(CONTAINER_LOAD_STEPS.validationPlanResolved);
      this.preValidationComplete.set(true);

      // Recalculate filtered sheets after all data is populated
      this.filteredSheets = this.getEnabledSheets();

      // Land on the right sheet now that filteredSheets are fully computed.
      // An explicit ?sheet= request (a field-link deep-link opened in a new
      // tab, or a refresh of the sheet the user was on) wins over the saved
      // sheet — on a cold load the init queryParams handler rejected it
      // because only filing-prerequisites was enabled during hydration, so
      // this is where it finally applies. The URL is left untouched in that
      // case so any accompanying ?scrollTo= survives for the destination
      // sheet to consume. Otherwise fall back to the saved sheet (refresh
      // restore) and sync the URL to it.
      const requestedSheet = this.getRequestedSheetFromUrl();
      // Hydration was kicked off by setConversionId in ngOnInit; ensure it
      // has resolved before reading the persisted current-sheet pointer.
      await this.stateService.whenHydrated();
      const savedSheet = await this.persistence.getCurrentSheet(this.conversionId() ?? '');
      if (requestedSheet && this.isSheetEnabled(requestedSheet)) {
        this.currentSheet = requestedSheet;
        this.persistence.putCurrentSheet(this.conversionId() ?? '', requestedSheet);
      } else if (savedSheet && this.isValidSheet(savedSheet) && this.isSheetEnabled(savedSheet as SheetType)) {
        this.currentSheet = savedSheet as SheetType;

        // Sync URL query param with restored sheet
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { ...this.route.snapshot.queryParams, sheet: savedSheet },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }
      // The initial landing decision is complete for every path — this is
      // the resolution point `enforcePhaseGatedSheet()`'s `?sheet=`
      // deep-link branch defers to. Idempotent; the non-deep-link paths
      // already set it earlier. See `landingSheetResolved`.
      this.landingSheetResolved.set(true);
      this.validateCurrentSheet();

      this.cdr.detectChanges();

      // The restored sheet reveals its own errors from its init; there is
      // nothing to trigger from here. `cdr.detectChanges()` above creates
      // the child synchronously, but its async `ngOnInit` has NOT assigned
      // a FormGroup by the next microtask, which is exactly why the old
      // container-side reveal missed on a refresh.

      // End API data loading phase - resume normal edited marking
      this.stateService.endAPIDataLoading();

      // End batch mode - flush all state to localStorage in a single write
      this.stateService.endBatchMode();

      // Snapshot all sheets' baselines to ensure original matches modified after all
      // transformations, calculations, and default applications are complete.
      // This prevents false positives in hasAnySheetModified().
      this.stateService.snapshotAllBaselines();

      // Reconcile the sheet-selector pencil against the caches this load
      // just rewrote. The pencil is DERIVED from (modified, original), and
      // everything above rewrote both: `loadFromAPI` replaced each sheet's
      // modified slice with the backend value, and `snapshotAllBaselines()`
      // re-based the rest. Hydration, meanwhile, may already have raised the
      // pencil from the PERSISTED diff via the one-way ratchet
      // (`hydrateEditedStatusFromCaches`) — correctly at the time, since the
      // unsubmitted edit was still in IndexedDB. Without this call that flag
      // outlives the diff it was derived from: the reloading tab kept a
      // pencil on a sheet whose caches now agree, and only a cross-tab
      // broadcast (which routes through `applyRemoteChange`'s two-way
      // recompute) could clear it — so the icon corrected itself only when a
      // SIBLING tab was refreshed.
      //
      // Two-way, so this cannot invent a pencil either: a sheet still
      // differing from its baseline keeps one. Deliberately AFTER
      // `snapshotAllBaselines()` — that call skips sheets flagged edited, so
      // reconciling earlier would read caches it had not finished settling.
      this.stateService.recomputeEditedStatusFromCaches();

      // Get the original state from BehaviorSubject (should have all transformed data now)
      const originalState = this.stateService.getOriginalState();

      // EXPLICITLY persist the original state (now via IndexedDB through
      // FilingFormPersistenceService) so it is saved even if loadFromAPI
      // didn't populate it.
      this.stateService.saveOriginalState(originalState);

      // Mark this conversion's data as loaded for the current JS context.
      // Subsequent navigate-backs into the same conversion will skip
      // fetchAllMbrsData() and go through replayPostLoadUiWiring() instead,
      // preserving any in-memory unsubmitted edits.
      this.stateService.markDataLoadedForCurrentConversion();

      // Terminal step — marking it closes the trace as a success and cancels
      // the stall watchdog.
      trace.mark(CONTAINER_LOAD_STEPS.landingResolved);
    }
  }


  /**
   * A `scenarios.config.json` failure on the navigate-back replay path, which has
   * no load trace of its own. A trace is opened ONLY to report (and is closed by
   * `fail` in the same call), so it can never stay open and hold the page-entry
   * clock waiting for a container load that is not coming. The hydration flag and
   * the loading overlay are released so nothing waits on a load that has ended.
   */
  private failReplayOnScenarios(error: unknown): void {
    const failure = error instanceof SchemaAssetError ? error.failure : 'PARSE';
    const trace = this.loadTrace.beginContainerLoad(this.conversionId(), () =>
      this.resolveLoadFailureSheetScope(),
    );
    trace.fail(failure, error);
    this.stateService.endMbrsHydration();
    this.mbrsLoadSettled.set(true);
    this.loadProgress.end();
    this.handleContainerLoadFailure(failure, trace.failureSummary);
  }

  /**
   * Navigate-back replay path for fetchAllMbrsData().
   *
   * When the user navigates back into a conversion whose backend data is
   * already loaded in memory (tracked via FilingFormStateService's
   * sessionLoadedConversionId), we deliberately SKIP the re-fetch to preserve
   * any unsubmitted edits. But we still need to re-run the post-load UI
   * wiring so the sheet selector, validation icons, and current-sheet
   * validation refresh correctly on the new component instance.
   *
   * This method intentionally does NOT call: startAPIDataLoading, loadFromAPI,
   * startBatchMode/endBatchMode, snapshotAllBaselines, or saveOriginalState.
   * The in-memory state from the original fetchAllMbrsData() run is still
   * authoritative for this JS context.
   */
  private async replayPostLoadUiWiring(): Promise<void> {
    // The scenarios file decides which sheets are hidden, so the replay waits for
    // it exactly as the fetch path does and a failure is just as fatal.
    try {
      await this.awaitScenarios();
    } catch (error) {
      this.failReplayOnScenarios(error);
      return;
    }

    this.stateService.endMbrsHydration();
    this.mbrsLoadSettled.set(true);

    this.loadProgress.reportPhase('Restoring filing workspace...');

    // Recompute enabled sheets from current state (Role 0 status, filing mode, etc.)
    this.filteredSheets = this.getEnabledSheets();

    // Pre-validation was already completed by the original fetch — keep it flagged
    // so status indicators render (skeleton loaders are not needed).
    this.preValidationComplete.set(true);

    // Restore saved sheet selection (handles the case where the user switched
    // sheets, left the filing, and came back — currentSheet must match the URL/
    // persisted entry for the same-sheet render to stay consistent). Hydration
    // was already kicked off by setConversionId; await it (idempotent — resolves
    // immediately once loaded) before reading the persisted pointer.
    await this.stateService.whenHydrated();
    const savedSheet = await this.persistence.getCurrentSheet(this.conversionId() ?? '');
    if (
      savedSheet &&
      this.isValidSheet(savedSheet) &&
      this.isSheetEnabled(savedSheet as SheetType)
    ) {
      this.currentSheet = savedSheet as SheetType;
    }

    this.validateCurrentSheet();
    this.cdr.detectChanges();

    // The visible sheet reveals its own errors from its init — see the note on
    // `ngAfterViewInit`. This timer now exists only to close the load overlay.
    setTimeout(() => {
      // Fast path — no backend fetch ran, so the overlay must be closed
      // explicitly here. The grace delay in the overlay component hides
      // the flash for this near-instant re-entry.
      this.loadProgress.end();
    }, 0);
  }

  /**
   * Resolve a human-readable sheet label for the load-progress overlay.
   * Looks up the label from {@link sheets} (the selector's visible list)
   * and falls back to a small map for values that are hidden from the
   * selector but still hydrated during initial load.
   */
  private resolveSheetDisplayLabel(value: SheetType): string {
    const fromSelector = this.sheets.find((sheet) => sheet.value === value)?.label;
    if (fromSelector) return fromSelector;
    // Fallback for sheet values that are hydrated but never reach the
    // selector baseline (e.g., alternate-layout variants the user never
    // lands on for a given submission type). Both filing-prerequisites
    // and filing-info are in the baseline `this.sheets`, so filing-info
    // no longer needs a fallback entry here.
    const hiddenLabels: Partial<Record<SheetType, string>> = {};
    return hiddenLabels[value] ?? value;
  }

  /**
   * Apply sheet validation statuses from the API response.
   * Maps backend SheetSlug values to frontend FilingFormState keys.
   */
  /**
   * Determine the current taxonomy from filing info data.
   * Checks TypeOfSubmission for MPERS indicator, defaults to MFRS.
   */
  private determineTaxonomy(): 'MFRS' | 'MPERS' {
    const submissionType = this.stateService.getTypeOfSubmission();
    if (submissionType.toUpperCase().includes('MPERS')) return 'MPERS';
    return 'MFRS';
  }

  /**
   * Populate the per-sheet status map for the loaded conversion.
   *
   * Phase 2F replacement of the legacy runBackgroundValidationCycle, which
   * mounted each role component sequentially in a hidden DOM container and
   * waited for it to call updateSheetLevelStatus(). With every sheet now
   * registered as a pure validator (PureValidatorRegistry covers all 26
   * keys), the orchestrator runs the entire registry against the editor's
   * already-loaded FilingFormState in <1s and returns the full
   * SheetLevelStatusMap synchronously. Result is persisted via
   * ConversionValidationCacheService.set so the listing/dashboard pages
   * see authoritative per-sheet statuses on the next visit.
   *
   * Behaviour preserved:
   *   - One-shot per conversion load (validationCycleCompleted guard).
   *   - All sheet statuses locked after publish to block late writes from
   *     surviving setTimeout callbacks in role components.
   *   - Loader overlay phases ("Validating sheets" -> "Finalizing").
   *
   * Behaviour removed (now obsolete):
   *   - Hidden component-mount loop and its accompanying validationSheet
   *     signal / template branch — pure validators do not need the DOM.
   *   - waitForSheetValidation event-bus polling — pure validators are
   *     synchronous, no event to wait for.
   *   - Partial-cache mid-cycle resume — total runtime is ~1s, so the
   *     resume optimisation no longer pays for its complexity.
   */
  private runBackgroundValidationCycle(): void {
    if (this.validationCycleCompleted) return;

    this.isRunningValidationCycle = true;
    this.loadProgress.reportPhase('Validating sheets');

    try {
      const conversionId = this.stateService.getConversionId();
      const sheetDataByStateKey = new Map<keyof FilingFormState, Record<string, any>>(
        Object.entries(this.stateService.exportAllData()) as [
          keyof FilingFormState,
          Record<string, any>
        ][]
      );

      // Per-sheet immutable N8N baseline (the same control-name-keyed snapshot
      // the mounted editor's `validateAgainstApiData` reads). Passing it lets the
      // headless Validated* calc-diff compare recomputed totals against the raw
      // extraction instead of the current persisted slice — without it, a
      // sum-derived total the editor already recomputed-and-persisted equals its
      // own recompute, so the "extracted ≠ calculated" warning never reaches the
      // submitted `hasSuggestedChanges`. Only the monetary sheets store a
      // snapshot; the rest are omitted and fall back to the persisted slice.
      const snapshotByStateKey = new Map<keyof FilingFormState, Record<string, any>>();
      for (const stateKey of sheetDataByStateKey.keys()) {
        const snap = this.stateService.getSnapshotSheetData(stateKey);
        if (snap && Object.keys(snap).length > 0) {
          snapshotByStateKey.set(stateKey, snap);
        }
      }

      // Pass the current phase so Phase 1 validates only filingPrerequisites
      // (the sole sheet revealed in Phase 1). Without it, the registry runs
      // over all 26 sheets and writes false `hasErrors: true` for every empty
      // downstream sheet into the validation cache the listing reads.
      const statuses = this.conversionValidator.validateLoadedConversion(
        conversionId,
        sheetDataByStateKey,
        this.stateService.getConversionPhase() ?? undefined,
        snapshotByStateKey
      );

      // Publish all 26 statuses in one batch to avoid 26 separate CD passes.
      // Late writes from any surviving setTimeout callbacks (e.g. an old role
      // component being torn down) are blocked by the per-sheet lock below.
      this.stateService.batchUpdateSheetLevelStatus(statuses);
      // Authoritative, applicability-scoped cover — lets the debounced persist
      // keep `complete: true` instead of demoting the entry this cycle just
      // wrote. Without it the fast path above is unreachable outside a single
      // debounce window.
      this.stateService.markFullValidationPublished(Object.keys(statuses));

      for (const stateKey of Object.keys(statuses) as (keyof FilingFormState)[]) {
        this.stateService.markSheetValidationComplete(stateKey as string);
        this.stateService.lockSheetStatus(stateKey as string);
      }

      this.validationCycleCompleted = true;
      this.cdr.detectChanges();
    } finally {
      this.isRunningValidationCycle = false;
      this.loadProgress.reportPhase('Finalizing...');
      this.loadProgress.end();
    }
  }

  /**
   * Decide how much of a cached `mbrs-validation-cache-<id>` entry can be
   * trusted for this load: which statuses to paint, which sheets still need
   * validating, and whether the background validation cycle can be skipped
   * outright.
   *
   * Pure by design (no state reads) so the decision is unit-testable without
   * mounting the container — see
   * `filing-form-container-validation-cache-plan.spec.ts`.
   *
   * The `gateOnly` guard is the fix for the reported "every sheet shows a red
   * cross except Filing Information" defect. `enabledStateKeys` comes from
   * `getEnabledSheets()`, which hard-blocks to the gate sheet alone until the
   * gate sheet's own status is established. When that happens, `missingStateKeys`
   * is trivially empty (the single enabled key is cached), the hit test collapses
   * to `hasEntry && complete`, and `filteredCached` narrows to that one key — so
   * the branch applies ONE status, leaves all 25 others on
   * `initialSheetLevelStatus`' pessimistic `hasErrors: true` seed, and sets
   * `validationCycleCompleted` so nothing ever corrects them.
   *
   * Ordering makes that unreachable now (the gate sheet is seeded before
   * `getEnabledSheets()`), but the guard stays as the invariant: the gate sheet
   * alone is never grounds for skipping validation, however complete the cache
   * claims to be. Running the cycle is the safe fallback — it is pure validators
   * over already-loaded state.
   *
   * Why it took two simultaneous loads to surface: `validateLoadedConversion`
   * writes `complete: true`, then the debounced persist in FilingFormStateService
   * rewrites the same key as `complete: false` one
   * CROSS_SHEET_VALIDATION_DEBOUNCE_MS later. A load only ever observes
   * `complete: true` inside that window, which is why middle-clicking a
   * conversion link twice in quick succession reproduces it and a single load
   * does not.
   */
  private resolveValidationCachePlan(
    // Structural rather than the full ConversionValidationCacheEntry:
    // `loadValidationCache()` hands back only these two fields.
    cachedEntry: { statuses: SheetLevelStatusMap; complete: boolean } | null,
    enabledStateKeys: string[],
    activeFilingInfoStateKey: string,
  ): {
    filteredCached: SheetLevelStatusMap | null;
    missingStateKeys: string[];
    isFullCacheHit: boolean;
  } {
    // Filter cached statuses to only those still enabled for this filing
    // (e.g., liquidity variants may have been toggled between sessions).
    // Any cached entries for now-disabled sheets are silently dropped.
    const enabledKeySet = new Set(enabledStateKeys);
    const filteredCached: SheetLevelStatusMap | null = cachedEntry
      ? Object.fromEntries(
          Object.entries(cachedEntry.statuses).filter(([k]) => enabledKeySet.has(k))
        ) as SheetLevelStatusMap
      : null;

    // Determine which enabled sheets still need validation (not covered by cache).
    const missingStateKeys = filteredCached
      ? enabledStateKeys.filter(stateKey => !(stateKey in filteredCached))
      : enabledStateKeys;

    // Degenerate enabled set: nothing, or the gate sheet by itself. Either way
    // the real applicable set is not yet knowable, so `missingStateKeys` being
    // empty proves nothing. A lone NON-gate sheet is a genuine applicability
    // outcome and is deliberately not caught here.
    const gateOnly =
      enabledStateKeys.length === 0 ||
      (enabledStateKeys.length === 1 &&
        enabledStateKeys[0] === activeFilingInfoStateKey);

    // A full cache hit requires: cache exists, was marked complete, AND every
    // currently-enabled sheet is represented. If the enabled set grew since the
    // cache was written (e.g., user switched taxonomy), we fall through to partial.
    const isFullCacheHit =
      !!cachedEntry &&
      cachedEntry.complete &&
      missingStateKeys.length === 0 &&
      !gateOnly;

    return { filteredCached, missingStateKeys, isFullCacheHit };
  }

  private applyApiSheetStatuses(
    sheetStatuses: { [sheetSlug: string]: MbrsStatusEnum },
    excludeStateKeys?: Set<string>,
    applicableStateKeys?: Set<string>,
  ): void {
    // Derived from SHEET_DESCRIPTORS (single source of truth) so this inbound
    // map cannot drift from the submit-side slug map. `FilingInfo` resolves to
    // `filingInfo` (not the Phase-1 prerequisites slot), matching the prior
    // hand-maintained literal — pinned in sheet-descriptors.spec.ts.
    const slugToStateKey: { [slug: string]: keyof FilingFormState } =
      buildSubmitSlugToStateKey();

    const updates: Partial<Record<keyof FilingFormState, Partial<SheetLevelStatusData>>> = {};
    for (const [slug, status] of Object.entries(sheetStatuses)) {
      const stateKey = slugToStateKey[slug];
      if (!stateKey) continue;
      // Skip sheets whose status was already restored from the local cache.
      // The cached value reflects the user's most recent local validation run
      // (potentially including unsaved edits), which is fresher than the API
      // snapshot taken at page load. Letting the API overwrite cached values
      // would regress partially-resumed runs back to the backend's view.
      if (excludeStateKeys && excludeStateKeys.has(stateKey as string)) continue;
      // Drop sheets this filing does not use. Omitting the scope applies
      // everything (the pre-scope behaviour) — silently dropping every status
      // would be a worse failure than the one this guard prevents.
      if (applicableStateKeys && !applicableStateKeys.has(stateKey as string))
        continue;
      // `status` is an `MbrsStatusEnum` int (was a boolean before the BE moved
      // per-sheet status to the shared enum). Derive the two local flags by
      // explicit comparison — a bare `!status` would mis-read Review(3) and
      // SuccessWithWarnings(6) (both truthy) as "no errors". The warnings-only
      // case (6) flips `hasSuggestedChanges`, which is what renders the red
      // asterisk beside the sheet's green tick on page load (no local
      // re-validation needed).
      updates[stateKey] = {
        hasErrors: status === MbrsStatusEnum.Review,
        hasSuggestedChanges: status === MbrsStatusEnum.SuccessWithWarnings,
      };
    }
    this.stateService.batchUpdateSheetLevelStatus(updates);
    // Note: sheetLevelStatus$ subscription handles stateVersion++, refreshSheetStatusCache, and detectChanges
  }

  /**
   * Get the label for a sheet type (for display in loading overlay)
   */
  getSheetLabel(sheetType: SheetType | null): string {
    if (!sheetType) return '';
    const sheet = this.sheets.find(sheet => sheet.value === sheetType);
    return sheet ? sheet.label : sheetType;
  }

}
